import React, { useEffect, useRef, useState } from "react";
import { createChart, CrosshairMode, LineStyle } from "lightweight-charts";
import api from "../api/client";
import useAppVisibility from "../hooks/useAppVisibility";
import { schedulePersistClientSettings } from '../context/userSettingsSync';
import { computeToolbarTrend } from '../utils/toolbarTrend';
import { resolveQuoteDecision, shouldApplyLiveQuote, getUsSession } from '../utils/premarketQuote';
import { computeVisibleRangeForPeriod, mapChartBars, parseUTCDate as parseChartUTCDate, toUtcTimestamp } from '../utils/chartParity';

// predictions: {
//   symbol: string,
//   market: string,
//   model: string, // selected model
//   docs: Array<{
//     created_at: string,
//     model: string,
//     forward_trend: string,
//     confidence: number,
//     preds: Array<{ date: string, value: number }>
//   }>
// }
const CandlestickChart = ({
  data = [],
  indicators = {},
  predictions = null,
  tradeSignals = null,
  theme = "light",
  height = 420,
  // Optional model selection controls (for placing next to Buy/Sell)
  availableModels = [],
  selectedModel = "",
  onChangeModel = null,
  symbol = null, // optional trading symbol for live quotes
  market = null, // optional market for quote routing/session logic
  stockInfo = null, // stock detail with model1_signal / model2_signal
}) => {
  // Local in-memory live quote cache with TTL (ms) to smooth spikes
  const LIVE_QUOTE_CACHE_TTL = 30 * 1000; // 30 seconds
  const liveQuoteCacheRef = useRef(new Map());
  // Load persisted indicator preferences (if any)
  const initialPrefs = (() => {
    try {
      const raw = localStorage.getItem('borsai.indicatorPrefs.v1');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  // Load style preferences (colors/widths/alphas)
  const initialStylePrefs = (() => {
    try {
      const raw = localStorage.getItem('borsai.indicatorStylePrefs.v1');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();
  const [stylePrefs, setStylePrefs] = useState(initialStylePrefs);

  const [showCandle, setShowCandle] = useState(() => initialPrefs?.showCandle ?? true);
  const [showLine, setShowLine] = useState(() => initialPrefs?.showLine ?? false);
  const [showVolume, setShowVolume] = useState(() => initialPrefs?.showVolume ?? true);
  const [showIndicatorsPanel, setShowIndicatorsPanel] = useState(() => initialPrefs?.showIndicatorsPanel ?? false);
  const [showAISignalsPanel, setShowAISignalsPanel] = useState(() => initialPrefs?.showAISignalsPanel ?? false);
  // Live signal is always enabled; UI toggle removed.
  const showLiveSignal = true;
  // Selected periods for MAs (tri-state via All checkbox + combobox)
  const MA_OPTIONS = [9, 20, 50, 100, 200];
  const sanitizePeriods = (arr) => (Array.isArray(arr) ? arr.filter((x) => [9, 20, 50, 100, 200].includes(x)) : []);
  const [smaPeriods, setSmaPeriods] = useState(() => sanitizePeriods(initialPrefs?.smaPeriods));
  const [emaPeriods, setEmaPeriods] = useState(() => {
    // If user has saved EMA prefs (including an empty array), respect them; otherwise default to all EMA periods
    if (initialPrefs && Array.isArray(initialPrefs.emaPeriods)) {
      return sanitizePeriods(initialPrefs.emaPeriods);
    }
    return [...MA_OPTIONS];
  });
  const [kamaPeriods, setKamaPeriods] = useState(() => sanitizePeriods(initialPrefs?.kamaPeriods));
  const smaAllRef = useRef(null);
  const emaAllRef = useRef(null);
  const kamaAllRef = useRef(null);
  // Dropdown state + refs for MA comboboxes
  const [showEmaOptions, setShowEmaOptions] = useState(false);
  const [showSmaOptions, setShowSmaOptions] = useState(false);
  const [showKamaOptions, setShowKamaOptions] = useState(false);
  const emaDropdownRef = useRef(null);
  const smaDropdownRef = useRef(null);
  const kamaDropdownRef = useRef(null);
  const [enableBollinger, setEnableBollinger] = useState(() => initialPrefs?.enableBollinger ?? false);
  const [enableIchimoku, setEnableIchimoku] = useState(() => initialPrefs?.enableIchimoku ?? false);
  const [enableRSIBands, setEnableRSIBands] = useState(() => {
    try {
      if (typeof initialPrefs?.enableRSIBands !== 'undefined') return initialPrefs.enableRSIBands;
      if (Array.isArray(data) && data.length) {
        const has = data.some(d => d && (
          d.rsib_lower != null || d.rsib_mid != null || d.rsib_upper != null ||
          d.rsibLower != null || d.rsibMid != null || d.rsibUpper != null
        ));
        if (has) return true;
      }
    } catch {}
    return false;
  });
  const [enableMACD, setEnableMACD] = useState(() => initialPrefs?.enableMACD ?? false);
  const [enableRSI, setEnableRSI] = useState(() => initialPrefs?.enableRSI ?? false);
  const [enableADX, setEnableADX] = useState(() => initialPrefs?.enableADX ?? false);
  // Divergences visibility toggles (default OFF for hidden/regular)
  const [enableHiddenDiv, setEnableHiddenDiv] = useState(() => initialPrefs?.enableHiddenDiv ?? false); // H
  const [enableRegularDiv, setEnableRegularDiv] = useState(() => initialPrefs?.enableRegularDiv ?? false); // G
  const [enableKnoxvilleDiv, setEnableKnoxvilleDiv] = useState(() => initialPrefs?.enableKnoxvilleDiv ?? true); // K
  const [enableKnoxvilleBarsBack, setEnableKnoxvilleBarsBack] = useState(() => initialPrefs?.enableKnoxvilleBarsBack ?? true);
  // Divergence-level (dvl) toggles
  const [enableDvlHiddenDiv, setEnableDvlHiddenDiv] = useState(() => initialPrefs?.enableDvlHiddenDiv ?? true);
  const [enableDvlRegularDiv, setEnableDvlRegularDiv] = useState(() => initialPrefs?.enableDvlRegularDiv ?? true);
  // Trade signals (BUY/SELL) overlay toggle
  const [enableTradeSignals, setEnableTradeSignals] = useState(() => initialPrefs?.enableTradeSignals ?? true);
  // Visible range period selection
  const [selectedPeriod, setSelectedPeriod] = useState(() => initialPrefs?.selectedPeriod || '6M'); // '1M' | '3M' | '6M' | 'YTD' | '1Y' | 'ALL'
  // A generation tick to signal chart (re)creation so overlays can rebuild reliably
  const [chartGen, setChartGen] = useState(0);
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const lineSeriesRefs = useRef([]);
  const predictionSeriesRef = useRef(null);
  const hoverPredictionSeriesRef = useRef(null);
  const hoverMarkersSeriesRef = useRef(null);
  const lastLiveRef = useRef(null); // buffer last live candle if series not ready yet
  const lastLiveDailyRef = useRef(null); // persist last applied daily candle to survive chart rebuilds
  const livePollingRef = useRef(null);
  const priceLineRef = useRef(null);
  const hoverDivRef = useRef(null);
  const trendDivRef = useRef(null);
  const infoBoxRef = useRef(null);
  const tradeOverlayRef = useRef(null);
  const cloudOverlayRef = useRef(null);
  const futureShadeRef = useRef(null);
  const shadeHandlerRef = useRef(null);
  // Track current price scale margins so we can adjust them dynamically (e.g., for divergence visibility)
  const priceScaleMarginsRef = useRef({ top: 0.08, bottom: 0.08 });
  // (Note) any stray top-level render blocks were removed; rendering happens inside effects below.
  const crosshairHandlerRef = useRef(null);
  const isHandlingRef = useRef(false);
  const mappedDataRef = useRef([]); // store mapped OHLC for auxiliary effects
  const divergenceCoordSeriesRef = useRef(null); // hidden series used for priceToCoordinate when candle hidden
  // Refs to read latest divergence toggle states inside crosshair handler without re-creating chart
  const enableHiddenDivRef = useRef(false);
  const enableRegularDivRef = useRef(false);
  const enableKnoxvilleDivRef = useRef(false);
  const enableDvlHiddenDivRef = useRef(false);
  const enableDvlRegularDivRef = useRef(false);
  const enableTradeSignalsRef = useRef(false);
  const userPinnedToRightRef = useRef(true); // track if user is at right edge to decide auto-scroll
  const showLiveSignalRef = useRef(true);
  const shouldAutoScrollRef = useRef(false); // only auto-scroll when user explicitly requests (dblclick)
  const debounceTimerRef = useRef(null);
  const savedRangeRef = useRef(null); // persist last visible range to restore on rebuilds

  const isAppVisible = useAppVisibility();

  // US (NYSE) market session detector (America/New_York wall clock).
  // - 'PRE': Mon-Fri before 09:30 ET
  // - 'REGULAR': Mon-Fri 09:30–16:00 ET
  // - 'POST': Mon-Fri 16:00–20:00 ET
  // - 'CLOSED': otherwise (incl. weekends). No holiday handling (as before).
  const usSessionNow = () => getUsSession(Date.now());

  useEffect(() => {
    // Live polling for latest candle/price (Alpha Vantage via backend proxy)
    let mounted = true;
  // Reduce quota usage: poll every 3 minutes (adjustable if needed)
  const pollInterval = 180 * 1000; // 180s
    const applyLive = (candle) => {
      try {
        if (!candle || !candle.time) return;
        const cs = candleSeriesRef.current;
        if (!cs) {
          // Buffer the latest live candle until the series is ready
          lastLiveRef.current = candle;
          return;
        }
        // For daily chart we aggregate the intraday minute candle into today's daily candle.
        // Compute date-only timestamp (UTC midnight) for the incoming candle time.
        const minuteTs = Number(candle.time);
        const dt = new Date(minuteTs * 1000);
  const dateStartTs = Math.floor(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()) / 1000);
      // keep silent in production

        // Determine aggregated OHLC for today's daily candle. Prefer existing open if present (historic or earlier intraday),
        // update high/low accordingly and set close to latest price.
        const mapped = Array.isArray(mappedDataRef.current) ? mappedDataRef.current : [];
  const existing = mapped.find((m) => Number(m.time) === dateStartTs);
      // keep silent in production

        let open = Number(candle.open);
        let high = Number(candle.high);
        let low = Number(candle.low);
        let close = Number(candle.close);

        if (existing) {
          // Use existing.open if it looks valid (non-zero), otherwise fallback to incoming open
          if (existing.open != null && Number(existing.open) !== 0) {
            open = Number(existing.open);
          }
          if (existing.high != null) high = Math.max(Number(existing.high), high);
          if (existing.low != null) low = Math.min(Number(existing.low), low);
        }

        // Update or append today's daily candle using the dateStartTs so we don't create duplicate timestamps
        cs.update({
          time: dateStartTs,
          open: open,
          high: high,
          low: low,
          close: close,
        });
        // Keep local mapped data in sync so other UI parts see today's candle
        try {
          const mapped = Array.isArray(mappedDataRef.current) ? [...mappedDataRef.current] : [];
          const idx = mapped.findIndex(m => Number(m.time) === dateStartTs);
          const nextBar = { time: dateStartTs, open, high, low, close, volume: Number(candle.volume || 0) };
          if (idx >= 0) mapped[idx] = { ...mapped[idx], ...nextBar };
          else mapped.push(nextBar);
          mapped.sort((a,b) => a.time - b.time);
          mappedDataRef.current = mapped;
          // Persist the last applied daily bar so we can re-apply it after chart rebuilds
          lastLiveDailyRef.current = nextBar;
        } catch {}
        // keep silent in production
        // Auto-scroll ONLY if user explicitly enabled via dblclick (shouldAutoScrollRef)
        try {
          if (chartRef.current && chartRef.current.timeScale && shouldAutoScrollRef.current) {
            try { chartRef.current.timeScale().scrollToRealTime(); } catch {}
          }
        } catch (e) {}
        // Persistent price marker: use hoverMarkersSeriesRef to show current price text (only if showLiveSignal is enabled)
        if (showLiveSignalRef.current && hoverMarkersSeriesRef.current) {
          hoverMarkersSeriesRef.current.setMarkers([{
            time: dateStartTs,
            position: 'inBar',
            color: '#f59e0b',
            shape: 'circle',
            text: Number(candle.close).toFixed(2),
          }]);
        } else if (!showLiveSignalRef.current && hoverMarkersSeriesRef.current) {
          // Clear markers when live signal is disabled
          hoverMarkersSeriesRef.current.setMarkers([]);
        }
      } catch (e) { console.warn('applyLive error', e); }
    };

    const handleQuotePayload = (json) => {
      // Decide which price source wins: the live provider quote or our DB's
      // latest daily candle.  During PRE/POST extended sessions we do NOT
      // let the DB override today's live/extended quote.
      const isUtcSameDaySec = (tsSec) => {
        try {
          if (!Number.isFinite(tsSec)) return false;
          const now = new Date();
          const todayStartSec = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) / 1000;
          return tsSec >= todayStartSec && tsSec < (todayStartSec + 86400);
        } catch {
          return false;
        }
      };

      const qTs = (json && typeof json.time !== 'undefined') ? Number(json.time) : null;
      const qIsToday = Number.isFinite(qTs) ? isUtcSameDaySec(qTs) : false;
      const sess = (json?.session || '').toString().toUpperCase();
      const extP = (typeof json?.extendedPrice === 'number' && Number.isFinite(json.extendedPrice)) ? json.extendedPrice : null;
      const isMarketOpen = json?.isMarketOpen === true;

      const session = usSessionNow();

      // If the provider reports the market is OPEN and its quote is for today,
      // ALWAYS apply live data to update the chart bar.
      if (qIsToday && isMarketOpen && json && typeof json.time !== 'undefined') {
        applyLive(json);
        return;
      }

      // Consider this an active extended-session quote if it reports PRE/POST and we have a usable extended price for today.
      const hasTodayExtended = qIsToday && (sess === 'PRE' || sess === 'POST') && extP != null;

      // If we have today's extended session data, apply it to show current price
      if (hasTodayExtended && json && typeof json.time !== 'undefined') {
        applyLive(json);
        return;
      }

      const arr = Array.isArray(mappedDataRef.current) ? mappedDataRef.current : [];
      const latestDbCandle = arr.length ? arr[arr.length - 1] : null;

      // Show the DB candle's close via the live marker without altering the series
      const applyDbCandle = (candle) => {
        if (!candle || !Number.isFinite(Number(candle.close))) return false;
        lastLiveDailyRef.current = candle;
        if (showLiveSignalRef.current && hoverMarkersSeriesRef.current) {
          hoverMarkersSeriesRef.current.setMarkers([{
            time: candle.time,
            position: 'inBar',
            color: '#f59e0b',
            shape: 'circle',
            text: Number(candle.close).toFixed(2),
          }]);
        }
        return true;
      };

      // Pre-market / stale-quote decision (shared with the header logic):
      // if the provider's quote is NOT from today (UTC), prefer the latest
      // DB daily candle when that candle is same-day or newer. This fixes
      // the "switch away and back" case where providers return the
      // previous day's quote during pre-market and would overwrite the
      // correct DB HLOC values.
      const decision = resolveQuoteDecision(json, latestDbCandle);
      if (decision.action === 'use-db' && applyDbCandle(latestDbCandle)) {
        return; // Prefer DB HLOC over the stale provider quote
      }

      // Outside regular hours, without usable extended data, keep the most
      // recent completed DB candle instead of overwriting it with the
      // provider's stale snapshot.
      if (session !== 'REGULAR' && !hasTodayExtended && applyDbCandle(latestDbCandle)) {
        return;
      }

      // Final gate (shared pure helper): a quote stamped "today" while the
      // market is CLOSED (e.g. weekends) is just the last completed daily
      // session (providers stamp their daily aggregate with a fresh "today"
      // timestamp, is_market_open=false).  Applying it as a live update would
      // replace today's (possibly empty) daily bar with yesterday's completed
      // OHLC — the "today" candle would vanish.  Never applyLive for that case.
      if (shouldApplyLiveQuote(json, session)) {
        applyLive(json);
      }
    };

    const fetchAndApply = async () => {
      if (!symbol) return;
      try {
        // Try local cache first
        const cacheKey = String(symbol).toUpperCase();
        const nowMs = Date.now();
        try {
          const entry = liveQuoteCacheRef.current.get(cacheKey);
          if (entry && (nowMs - entry.t) < LIVE_QUOTE_CACHE_TTL) {
            if (!mounted) return;
            handleQuotePayload(entry.data);
            return;
          }
        } catch {}

        const marketHint = market || ((predictions && typeof predictions.market === 'string') ? predictions.market : undefined);
        const res = await api.get('/quote', { params: { symbol, market: marketHint } });
        const json = res?.data;
        // Debug: log the raw payload received from the backend so we can verify fields/time
        if (!mounted) return;
        // Cache successful payloads
        try { if (json && typeof json.time !== 'undefined') liveQuoteCacheRef.current.set(cacheKey, { t: nowMs, data: json }); } catch {}
        handleQuotePayload(json);
      } catch (e) {
        console.warn('live fetch failed', e);
        // On failure, do not write 0s; rely on DB candle fallback paths already implemented above
      }
    };

    // Start/resume polling only when tab is visible; pause when hidden
    if (symbol && isAppVisible) {
      fetchAndApply();
      livePollingRef.current = setInterval(fetchAndApply, pollInterval);
    } else {
      try { if (livePollingRef.current) clearInterval(livePollingRef.current); } catch {}
      livePollingRef.current = null;
    }
    return () => {
      mounted = false;
      try { if (livePollingRef.current) clearInterval(livePollingRef.current); } catch {}
    };
    // only re-run when symbol changes
  }, [symbol, market, isAppVisible]);

  useEffect(() => {
    enableHiddenDivRef.current = enableHiddenDiv;
    enableRegularDivRef.current = enableRegularDiv;
    enableKnoxvilleDivRef.current = enableKnoxvilleDiv;
    enableDvlHiddenDivRef.current = enableDvlHiddenDiv;
    enableDvlRegularDivRef.current = enableDvlRegularDiv;
    enableTradeSignalsRef.current = enableTradeSignals;
    // Live signal is forced enabled
    showLiveSignalRef.current = true;
  }, [enableHiddenDiv, enableRegularDiv, enableKnoxvilleDiv, enableDvlHiddenDiv, enableDvlRegularDiv, enableTradeSignals]);

  // Debounced visible range handler to detect manual scroll; disable auto-scroll if user moves left
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const ts = chart.timeScale();
    const handler = () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        try {
          let pinned = true;
          const vr = ts.getVisibleRange();
          // Persist latest visible range for restoration across rebuilds
          if (vr && typeof vr.from !== 'undefined' && typeof vr.to !== 'undefined') {
            savedRangeRef.current = { from: Number(vr.from), to: Number(vr.to) };
          }
          const arr = Array.isArray(mappedDataRef.current) ? mappedDataRef.current : [];
          const last = arr.length ? Number(arr[arr.length - 1].time) : null;
          if (vr && typeof vr.to !== 'undefined' && last) {
            const tolerance = 24 * 60 * 60; // 1 day in seconds
            pinned = Number(vr.to) >= (last - tolerance);
          }
          // If not pinned anymore, stop auto-scroll until user dblclicks time axis
          if (!pinned) shouldAutoScrollRef.current = false;
        } catch {}
      }, 150);
    };
    ts.subscribeVisibleTimeRangeChange(handler);
    return () => {
      try { ts.unsubscribeVisibleTimeRangeChange(handler); } catch {}
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [chartGen]);

  // Ensure live marker is restored on mount (live signal is always enabled)
  useEffect(() => {
    if (!hoverMarkersSeriesRef.current) return;
    const lastDaily = lastLiveDailyRef.current;
    if (lastDaily && lastDaily.time && lastDaily.close) {
      hoverMarkersSeriesRef.current.setMarkers([{
        time: lastDaily.time,
        position: 'inBar',
        color: '#f59e0b',
        shape: 'circle',
        text: Number(lastDaily.close).toFixed(2),
      }]);
    }
  }, []);

  // Persist preferences whenever they change
  useEffect(() => {
    try {
      const prefs = {
        showCandle,
        showLine,
        showVolume,
        showIndicatorsPanel,
        showAISignalsPanel,
        smaPeriods,
        emaPeriods,
        kamaPeriods,
        enableBollinger,
        enableIchimoku,
        enableRSIBands,
        enableMACD,
        enableRSI,
        enableADX,
        enableHiddenDiv,
        enableRegularDiv,
        enableKnoxvilleDiv,
        enableKnoxvilleBarsBack,
        enableDvlHiddenDiv,
        enableDvlRegularDiv,
        enableTradeSignals,
        selectedPeriod,
      };
      localStorage.setItem('borsai.indicatorPrefs.v1', JSON.stringify(prefs));

      // Persist to DB as well (debounced)
      try {
        schedulePersistClientSettings({
          localStorage: {
            'borsai.indicatorPrefs.v1': prefs,
          },
        }, 250);
      } catch {}
    } catch {}
  }, [
    showCandle,
    showLine,
    showVolume,
    showIndicatorsPanel,
    showAISignalsPanel,
    smaPeriods,
    emaPeriods,
    kamaPeriods,
    enableBollinger,
    enableIchimoku,
    enableRSIBands,
    enableMACD,
    enableRSI,
    enableADX,
    enableHiddenDiv,
    enableRegularDiv,
    enableKnoxvilleDiv,
    enableKnoxvilleBarsBack,
    enableDvlHiddenDiv,
    enableDvlRegularDiv,
    enableTradeSignals,
    selectedPeriod,
  ]);

  // Apply visible range based on selected period
  const applyPeriod = React.useCallback((period) => {
    const chart = chartRef.current;
    const mapped = Array.isArray(mappedDataRef.current) ? mappedDataRef.current : [];
    if (!chart || !mapped.length) return;
    const timeScale = chart.timeScale();
    const range = computeVisibleRangeForPeriod(mapped, predictions, period);
    if (!range) return;
    try { timeScale.setVisibleRange(range); } catch {}
  }, [predictions]);

  // Re-apply visible range when selection changes or chart is recreated
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const ts = chart.timeScale();
    const saved = savedRangeRef.current;
    // If the user manually scrolled/zoomed (we have a saved range) and hasn't opted into auto-follow,
    // restore their exact range instead of reapplying the period.
    if (saved && !shouldAutoScrollRef.current) {
      try { ts.setVisibleRange(saved); return; } catch {}
    }
    // Otherwise, apply the selected period and honor auto-follow if enabled
    applyPeriod(selectedPeriod);
    if (shouldAutoScrollRef.current) {
      try { ts.scrollToRealTime(); } catch {}
    }
  }, [selectedPeriod, applyPeriod, chartGen, predictions]);
  // Keep All checkboxes in tri-state based on selection
  useEffect(() => {
    const setIndeterminate = (ref, arr) => {
      if (!ref?.current) return;
      const all = arr.length === MA_OPTIONS.length;
      const none = arr.length === 0;
      ref.current.indeterminate = !all && !none;
    };
    setIndeterminate(smaAllRef, smaPeriods);
    setIndeterminate(emaAllRef, emaPeriods);
    setIndeterminate(kamaAllRef, kamaPeriods);
  }, [smaPeriods, emaPeriods, kamaPeriods]);

  // Close dropdowns on outside click or ESC, and when indicators panel hides
  useEffect(() => {
    const onDocMouseDown = (e) => {
      const t = e.target;
      const inside = (node) => node && (node === t || node.contains(t));
      if (!inside(emaDropdownRef.current)) setShowEmaOptions(false);
      if (!inside(smaDropdownRef.current)) setShowSmaOptions(false);
      if (!inside(kamaDropdownRef.current)) setShowKamaOptions(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setShowEmaOptions(false);
        setShowSmaOptions(false);
        setShowKamaOptions(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  useEffect(() => {
    if (!showIndicatorsPanel) {
      setShowEmaOptions(false);
      setShowSmaOptions(false);
      setShowKamaOptions(false);
    }
  }, [showIndicatorsPanel]);
  useEffect(() => {
    const onKey = (e) => {
      // Don't handle shortcuts when typing in inputs/textareas or when modifier keys are pressed
      const target = e.target;
      const isEditable = (
        (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT')) ||
        (target && typeof target.isContentEditable === 'boolean' && target.isContentEditable)
      );
      if (isEditable || e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;

      const key = (e.key || '').toLowerCase();
      if (key === 'c') {
        setShowCandle((prev) => {
          const next = !prev;
          if (!next && !showLine) return true; // ensure at least one of Candle/Line
          return next;
        });
      } else if (key === 'l') {
        setShowLine((prev) => {
          const next = !prev;
          if (!next && !showCandle) return true;
          return next;
        });
      } else if (key === 'v') {
        setShowVolume((prev) => !prev);
      } else if (key === 'b') {
        setEnableBollinger((prev) => !prev);
        setShowIndicatorsPanel(true);
      } else if (key === 'i') {
        setEnableIchimoku((prev) => !prev);
        setShowIndicatorsPanel(true);
      } else if (key === 'e') {
        // Toggle all/none EMAs
        setEmaPeriods(prev => (prev.length === MA_OPTIONS.length ? [] : [...MA_OPTIONS]));
        setShowIndicatorsPanel(true);
      } else if (key === 's') {
        // Toggle all/none SMAs
        setSmaPeriods(prev => (prev.length === MA_OPTIONS.length ? [] : [...MA_OPTIONS]));
        setShowIndicatorsPanel(true);
      } else if (key === 'a') { // All KAMAs
        // Toggle all/none KAMAs
        setKamaPeriods(prev => (prev.length === MA_OPTIONS.length ? [] : [...MA_OPTIONS]));
        setShowIndicatorsPanel(true);
      } else if (key === 'm') {
        setEnableMACD((prev) => !prev);
        setShowIndicatorsPanel(true);
      } else if (key === 'r') {
        setEnableRSI((prev) => !prev);
        setShowIndicatorsPanel(true);
      } else if (key === 'h') {
        setEnableHiddenDiv((prev) => !prev);
        setShowIndicatorsPanel(true);
      } else if (key === 'g') {
        setEnableRegularDiv((prev) => !prev);
        setShowIndicatorsPanel(true);
      } else if (key === 'k') { // Knoxville divergence
        setEnableKnoxvilleDiv((prev) => !prev);
        setShowIndicatorsPanel(true);
      } else if (key === 'j') { // DVL Hidden divergences
        setEnableDvlHiddenDiv((prev) => !prev);
        setShowIndicatorsPanel(true);
      } else if (key === 'n') { // DVL Regular divergences
        setEnableDvlRegularDiv((prev) => !prev);
        setShowIndicatorsPanel(true);
      } else if (key === 't') { // Trade signals toggle
        setEnableTradeSignals((prev) => !prev);
        setShowAISignalsPanel(true);
      }
      else if (key === 'd') {
        setEnableADX((prev) => !prev);
        setShowIndicatorsPanel(true);
      }
      else if( key === 'x') { // Close indicators panel
        setShowIndicatorsPanel((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showCandle, showLine]);

  useEffect(() => {
    if (!containerRef.current) return;

    // Cleanup old chart and its series safely (remove series from the chart that created them)
    if (chartRef.current) {
      try {
        if (predictionSeriesRef.current) {
          try { chartRef.current.removeSeries(predictionSeriesRef.current); } catch {}
          predictionSeriesRef.current = null;
        }
        if (hoverPredictionSeriesRef.current) {
          try { chartRef.current.removeSeries(hoverPredictionSeriesRef.current); } catch {}
          hoverPredictionSeriesRef.current = null;
        }
        if (hoverMarkersSeriesRef.current) {
          try { chartRef.current.removeSeries(hoverMarkersSeriesRef.current); } catch {}
          hoverMarkersSeriesRef.current = null;
        }
        if (Array.isArray(lineSeriesRefs.current) && lineSeriesRefs.current.length) {
          lineSeriesRefs.current.forEach(s => { try { if (s) chartRef.current.removeSeries(s); } catch {} });
          lineSeriesRefs.current = [];
        }
        if (candleSeriesRef.current) {
          try { chartRef.current.removeSeries(candleSeriesRef.current); } catch {}
          candleSeriesRef.current = null;
        }
      } finally {
        try { chartRef.current.remove(); } catch {}
        chartRef.current = null;
      }
    }
    // Remove any floating DOM nodes from previous render
    if (hoverDivRef.current && hoverDivRef.current.parentNode) {
      try { hoverDivRef.current.parentNode.removeChild(hoverDivRef.current); } catch {}
      hoverDivRef.current = null;
    }
    if (trendDivRef.current && trendDivRef.current.parentNode) {
      try { trendDivRef.current.parentNode.removeChild(trendDivRef.current); } catch {}
      trendDivRef.current = null;
    }
    if (infoBoxRef.current && infoBoxRef.current.parentNode) {
      try { infoBoxRef.current.parentNode.removeChild(infoBoxRef.current); } catch {}
      infoBoxRef.current = null;
    }
    if (tradeOverlayRef.current && tradeOverlayRef.current.parentNode) {
      try { tradeOverlayRef.current.parentNode.removeChild(tradeOverlayRef.current); } catch {}
      tradeOverlayRef.current = null;
    }
    if (cloudOverlayRef.current && cloudOverlayRef.current.parentNode) {
      try { cloudOverlayRef.current.parentNode.removeChild(cloudOverlayRef.current); } catch {}
      cloudOverlayRef.current = null;
    }

  const isDark = theme === "dark";

  const chart = createChart(containerRef.current, {
      height,
      layout: {
        background: { type: "Solid", color: isDark ? "#111827" : "#ffffff" },
        textColor: isDark ? "#d1d5db" : "#111827",
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: isDark ? "#1f2937" : "#e5e7eb" },
        horzLines: { color: isDark ? "#1f2937" : "#e5e7eb" },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderVisible: false },
      timeScale: { borderVisible: false },
    });

  chartRef.current = chart;
    // Enable manual "jump to latest" via double-click on time axis or chart area
    try {
      const el = containerRef.current;
      if (el) {
        const onDbl = () => {
          shouldAutoScrollRef.current = true;
          // When opting into auto-follow, clear any saved manual range
          savedRangeRef.current = null;
          try { chart.timeScale().scrollToRealTime(); } catch {}
        };
        el.addEventListener('dblclick', onDbl);
        // Clean up on unmount/recreate
        const cleanupDbl = () => { try { el.removeEventListener('dblclick', onDbl); } catch {} };
        // Attach cleanup to chart remove
        try { (cleanupPrev._dbl = cleanupDbl); } catch { cleanupDbl(); }
      }
    } catch {}
    // Ensure overlay DOM (labels, tooltips) do not overflow outside chart area
    try {
      containerRef.current.style.position = containerRef.current.style.position || 'relative';
      containerRef.current.style.overflow = 'hidden';
    } catch {}

    const hoverPredictionSeries = chart.addLineSeries({
      color: stylePrefs?.prediction?.hoverLine?.color || '#f59e0b',
      lineWidth: stylePrefs?.prediction?.hoverLine?.width || 2,
      lineStyle: LineStyle.Dashed,
      pointMarkersVisible: true,
    });
    hoverPredictionSeriesRef.current = hoverPredictionSeries;

    const hoverMarkersSeries = chart.addLineSeries({
      color: '#f59e0b',
      lineWidth: 0,
      pointMarkersVisible: true,
    });
    hoverMarkersSeriesRef.current = hoverMarkersSeries;

    // We'll create the candlestick series AFTER drawing MA/Bollinger lines so those render beneath candles.
    let candle = null;

    const parseUTCDate = parseChartUTCDate;
    const toTs = toUtcTimestamp;

  const mapped = mapChartBars(data);
  // Save mapped data for downstream effects (divergence overlay, hover lookups)
  mappedDataRef.current = mapped;

    // Price line (close) overlay
    if (showLine) {
      const priceLine = chart.addLineSeries({ color: isDark ? '#93c5fd' : '#1d4ed8', lineWidth: 2 });
      priceLine.setData(mapped.map(p => ({ time: p.time, value: p.close })));
      lineSeriesRefs.current.push(priceLine);
    }

  // Compute dynamic sub-pane layout for Volume, MACD, RSI, ADX (stacked from bottom)
    const bottomPanesOrder = [];
    if (showVolume) bottomPanesOrder.push('volume');
  if (enableMACD) bottomPanesOrder.push('macd');
  if (enableRSI) bottomPanesOrder.push('rsi');
  if (enableADX) bottomPanesOrder.push('adx');
    // Base slice ~22%; make volume ~35% smaller than base to reduce its height
    const baseSlice = bottomPanesOrder.length ? Math.min(0.26, Math.max(0.16, 0.22)) : 0; // ~22%
    const volumeSlice = baseSlice * 0.65; // ~35% reduction
    const paneHeights = (id) => (id === 'volume' ? volumeSlice : baseSlice);
    const paneMargins = {};
    {
      let bottomAccum = 0;
      for (const id of bottomPanesOrder) {
        const h = paneHeights(id);
        const top = Math.max(0, 1 - (bottomAccum + h));
        const bottom = bottomAccum;
        paneMargins[id] = { top, bottom };
        bottomAccum += h;
      }
    }
  const totalBottomMargin = bottomPanesOrder.length
    ? Math.min(0.8, bottomPanesOrder.reduce((acc, id) => acc + paneHeights(id), 0))
    : 0;
  // Extra padding so prices don't touch top/bottom edges of the main pane
  const pricePaddingTop = 0.08;    // ~8% space at the top of main price pane
  const pricePaddingBottom = 0.04; // ~4% space between price pane and subpanes
    // We'll apply price scale margins after creating the candle series below

  // Volume histogram
    if (showVolume && mapped.length) {
      // Place volume on its own price scale at the bottom ~22% of the pane
      const volumeSeries = chart.addHistogramSeries({
        priceFormat: { type: 'volume' },
        color: isDark ? 'rgba(75,85,99,0.6)' : 'rgba(107,114,128,0.6)',
        priceScaleId: 'volume',
        base: 0,
        lastValueVisible: false,
      });
      try {
        const m = paneMargins.volume || { top: 0.78, bottom: 0 };
        volumeSeries.priceScale().applyOptions({
          scaleMargins: m,
          borderVisible: false,
        });
      } catch {}

      // Build raw volume data (no truncation) to match TradingView visuals
      const raw = mapped.map((p, i, arr) => {
        const prev = i > 0 ? arr[i - 1] : null;
        const up = prev ? p.close >= prev.close : p.close >= p.open;
        return { time: p.time, value: p.volume || 0, color: up ? 'rgba(16,185,129,0.6)' : 'rgba(239,68,68,0.6)' };
      });
      volumeSeries.setData(raw);
      lineSeriesRefs.current.push(volumeSeries);
    }

  // Divergences are now handled by a separate effect to avoid resetting the chart on toggle changes

  // Add simple overlays for common indicators if present in indicators param
  // Note: we cleared old overlays when tearing down the previous chart above.

      const addLine = (points, color = '#3b82f6', opts = {}) => {
        const { priceLineVisible = false, lineWidth = 2, lastValueVisible = true, priceScaleId, lineStyle } = opts || {};
        const seriesOpts = { color, lineWidth, priceLineVisible, lastValueVisible };
        if (priceScaleId) seriesOpts.priceScaleId = priceScaleId;
        if (typeof lineStyle !== 'undefined') seriesOpts.lineStyle = lineStyle;
        const ls = chart.addLineSeries(seriesOpts);
        ls.setData(points);
        lineSeriesRefs.current.push(ls);
      };

    const buildLine = (key, color, opts = {}) => {
      if (!indicators[key]) return;
      const points = indicators[key].map(p => {
        const parsedDate = parseUTCDate(p.date);
        const timestamp = Math.floor(parsedDate.getTime() / 1000);
        return { 
          time: timestamp, 
          value: Number(p.value) 
        };
      }).filter(p => !isNaN(p.time) && p.time > 0 && !isNaN(p.value))
        .sort((a, b) => a.time - b.time);
      
      if (points.length > 0) {
        addLine(points, color, opts);
      }
    };

    // Build line directly from historical data field key
    const buildLineFromData = (fieldKey, color, opts = {}) => {
      const points = data.map(p => {
        const parsedDate = parseUTCDate(p.date);
        const timestamp = Math.floor(parsedDate.getTime() / 1000);
        const v = p[fieldKey];
        return { time: timestamp, value: Number(v) };
      }).filter(p => !isNaN(p.time) && p.time > 0 && !isNaN(p.value))
        .sort((a, b) => a.time - b.time);
      if (points.length > 0) addLine(points, color, opts);
    };

    const buildIndicator = (key, color, opts = {}) => {
      // Prefer direct-from-data if present, else use precomputed indicators prop
      const anyHasField = Array.isArray(data) && data.length && data.some(p => typeof p[key] !== 'undefined');
      if (anyHasField) {
        buildLineFromData(key, color, opts);
      } else if (indicators[key]) {
        buildLine(key, color, opts);
      }
    };

  // Grouped indicators from historical data (drawn BEFORE candles to keep them beneath)
    // SMAs by selected periods
    if (smaPeriods.length) {
      const noOverlay = { priceLineVisible: false };
      if (smaPeriods.includes(9)) buildIndicator('sma_9_sma', stylePrefs?.sma?.['9']?.color || '#8fe5af', { ...noOverlay, lineWidth: stylePrefs?.sma?.['9']?.width || 2 });
      if (smaPeriods.includes(20)) buildIndicator('sma_20_sma', stylePrefs?.sma?.['20']?.color || '#22c55e', { ...noOverlay, lineWidth: stylePrefs?.sma?.['20']?.width || 2 });
      if (smaPeriods.includes(50)) buildIndicator('sma_50_sma', stylePrefs?.sma?.['50']?.color || '#16a34a', { ...noOverlay, lineWidth: stylePrefs?.sma?.['50']?.width || 2 });
      if (smaPeriods.includes(100)) buildIndicator('sma_100_sma', stylePrefs?.sma?.['100']?.color || '#059669', { ...noOverlay, lineWidth: stylePrefs?.sma?.['100']?.width || 2 });
      if (smaPeriods.includes(200)) buildIndicator('sma_200_sma', stylePrefs?.sma?.['200']?.color || '#065f46', { ...noOverlay, lineWidth: stylePrefs?.sma?.['200']?.width || 2 });
    }
    // EMAs by selected periods
    if (emaPeriods.length) {
      const noOverlay = { priceLineVisible: false };
      if (emaPeriods.includes(9)) buildIndicator('ema_9_ema', stylePrefs?.ema?.['9']?.color || '#1316db', { ...noOverlay, lineWidth: stylePrefs?.ema?.['9']?.width || 2 });
      if (emaPeriods.includes(20)) buildIndicator('ema_20_ema', stylePrefs?.ema?.['20']?.color || '#4caf50', { ...noOverlay, lineWidth: stylePrefs?.ema?.['20']?.width || 2 });
      if (emaPeriods.includes(50)) buildIndicator('ema_50_ema', stylePrefs?.ema?.['50']?.color || '#3179f5', { ...noOverlay, lineWidth: stylePrefs?.ema?.['50']?.width || 2 });
      if (emaPeriods.includes(100)) buildIndicator('ema_100_ema', stylePrefs?.ema?.['100']?.color || '#b22833', { ...noOverlay, lineWidth: stylePrefs?.ema?.['100']?.width || 2 });
      if (emaPeriods.includes(200)) buildIndicator('ema_200_ema', stylePrefs?.ema?.['200']?.color || '#4dd0e1', { ...noOverlay, lineWidth: stylePrefs?.ema?.['200']?.width || 2 });
    }
    // KAMAs by selected periods
    if (kamaPeriods.length) {
      const noOverlay = { priceLineVisible: false };
      if (kamaPeriods.includes(9)) buildIndicator('kama_9_kama', stylePrefs?.kama?.['9']?.color || '#f1a6cd', { ...noOverlay, lineWidth: stylePrefs?.kama?.['9']?.width || 2 });
      if (kamaPeriods.includes(20)) buildIndicator('kama_20_kama', stylePrefs?.kama?.['20']?.color || '#f472b6', { ...noOverlay, lineWidth: stylePrefs?.kama?.['20']?.width || 2 });
      if (kamaPeriods.includes(50)) buildIndicator('kama_50_kama', stylePrefs?.kama?.['50']?.color || '#ec4899', { ...noOverlay, lineWidth: stylePrefs?.kama?.['50']?.width || 2 });
      if (kamaPeriods.includes(100)) buildIndicator('kama_100_kama', stylePrefs?.kama?.['100']?.color || '#db2777', { ...noOverlay, lineWidth: stylePrefs?.kama?.['100']?.width || 2 });
      if (kamaPeriods.includes(200)) buildIndicator('kama_200_kama', stylePrefs?.kama?.['200']?.color || '#be185d', { ...noOverlay, lineWidth: stylePrefs?.kama?.['200']?.width || 2 });
    }
    // RSI Bands on main chart (rsib_lower, rsib_mid, rsib_upper)
    const hasRsibData = Array.isArray(data) && data.length && data.some(d => d && (
      d.rsib_lower != null || d.rsib_mid != null || d.rsib_upper != null ||
      d.rsibLower != null || d.rsibMid != null || d.rsibUpper != null
    ));
    // Only render RSI Bands when the user explicitly enables them.
    if (enableRSIBands && hasRsibData) try {
      // RSI Bands on main chart (try several possible field name variants)
      const rsibLowerColor = stylePrefs?.rsib?.lower?.color || '#60a5fa';
      const rsibMidColor = stylePrefs?.rsib?.mid?.color || '#9ca3af';
      const rsibUpperColor = stylePrefs?.rsib?.upper?.color || '#f87171';
      const rsibLowerWidth = stylePrefs?.rsib?.lower?.width || 1;
      const rsibMidWidth = stylePrefs?.rsib?.mid?.width || 1;
      const rsibUpperWidth = stylePrefs?.rsib?.upper?.width || 1;

      const variants = (base) => ([
        base,
        base.replace(/_/g, '.'),
        base.replace(/_/g, ''),
        base.replace(/_([a-z])/g, (m, p1) => p1.toUpperCase()), // rsib_lower -> rsibLower
      ]);

      const findKey = (candidates) => {
        // Check data array first
        if (Array.isArray(data) && data.length) {
          for (const k of candidates) {
            if (data.some(d => typeof d?.[k] !== 'undefined')) return k;
          }
        }
        for (const k of candidates) {
          if (indicators && typeof indicators[k] !== 'undefined') return k;
        }
        return null;
      };

      const lowerKey = findKey(variants('rsib_lower'));
      const midKey = findKey(variants('rsib_mid'));
      const upperKey = findKey(variants('rsib_upper'));

      // In development, log which keys were found
      if (process.env.NODE_ENV === 'development') {
        // keep silent in production
      }

      if (lowerKey) buildLineFromData(lowerKey, rsibLowerColor, { priceLineVisible: false, lineWidth: rsibLowerWidth });
      else if (indicators['rsib_lower']) buildIndicator('rsib_lower', rsibLowerColor, { priceLineVisible: false, lineWidth: rsibLowerWidth });

      if (midKey) buildLineFromData(midKey, rsibMidColor, { priceLineVisible: false, lineWidth: rsibMidWidth });
      else if (indicators['rsib_mid']) buildIndicator('rsib_mid', rsibMidColor, { priceLineVisible: false, lineWidth: rsibMidWidth });

      if (upperKey) buildLineFromData(upperKey, rsibUpperColor, { priceLineVisible: false, lineWidth: rsibUpperWidth });
      else if (indicators['rsib_upper']) buildIndicator('rsib_upper', rsibUpperColor, { priceLineVisible: false, lineWidth: rsibUpperWidth });
    } catch (e) { /* ignore */ }
    if (enableBollinger) {
      const noOverlay = { priceLineVisible: false };
      buildIndicator('bb_upper', stylePrefs?.bb?.upper?.color || '#f87171', { ...noOverlay, lineWidth: stylePrefs?.bb?.upper?.width || 2, lineStyle: LineStyle.Dotted });
      buildIndicator('bb_middle', stylePrefs?.bb?.middle?.color || '#9ca3af', { ...noOverlay, lineWidth: stylePrefs?.bb?.middle?.width || 2, lineStyle: LineStyle.Dotted });
      buildIndicator('bb_lower', stylePrefs?.bb?.lower?.color || '#60a5fa', { ...noOverlay, lineWidth: stylePrefs?.bb?.lower?.width || 2, lineStyle: LineStyle.Dotted });
    }
    if (enableIchimoku) {
      buildIndicator('ichimoku_senkou_a', stylePrefs?.ichimoku?.a?.color || '#10b981', { lineWidth: stylePrefs?.ichimoku?.a?.width || 2 });
      buildIndicator('ichimoku_senkou_b', stylePrefs?.ichimoku?.b?.color || '#ef4444', { lineWidth: stylePrefs?.ichimoku?.b?.width || 2 });
    }

    // Now create candlesticks so candles render above MA/Bollinger lines
    if (showCandle) {
      candle = chart.addCandlestickSeries({
        upColor: stylePrefs?.candle?.upColor || '#16a34a',
        downColor: stylePrefs?.candle?.downColor || '#dc2626',
        wickUpColor: stylePrefs?.candle?.wickUpColor || '#16a34a',
        wickDownColor: stylePrefs?.candle?.wickDownColor || '#dc2626',
        borderVisible: false,
      });
      candleSeriesRef.current = candle;
      try { candle.setData(mapped); } catch {}
      // If a live candle was buffered before the series was ready, apply it now
      try {
        if (lastLiveRef.current && lastLiveRef.current.time) {
          const live = lastLiveRef.current;
          // Aggregate into today's daily candle
          const minuteTs = Number(live.time);
          const dt = new Date(minuteTs * 1000);
          const dateStartTs = Math.floor(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()) / 1000);
          const mappedArr = Array.isArray(mappedDataRef.current) ? mappedDataRef.current : [];
          const existing = mappedArr.find((m) => Number(m.time) === dateStartTs);
          let open = Number(live.open);
          let high = Number(live.high);
          let low = Number(live.low);
          let close = Number(live.close);
          if (existing) {
            if (existing.open != null && Number(existing.open) !== 0) open = Number(existing.open);
            if (existing.high != null) high = Math.max(Number(existing.high), high);
            if (existing.low != null) low = Math.min(Number(existing.low), low);
          }
          candleSeriesRef.current.update({ time: dateStartTs, open, high, low, close });
          // sync mapped
          try {
            const idx = mappedArr.findIndex(m => Number(m.time) === dateStartTs);
            const nextBar = { time: dateStartTs, open, high, low, close, volume: Number(live.volume || 0) };
            if (idx >= 0) mappedArr[idx] = { ...mappedArr[idx], ...nextBar };
            else mappedArr.push(nextBar);
            mappedArr.sort((a,b) => a.time - b.time);
            mappedDataRef.current = mappedArr;
            lastLiveDailyRef.current = nextBar;
          } catch {}
          // clear buffer after applying
          lastLiveRef.current = null;
        }
      } catch {}

      // If we previously applied a live daily candle (persisted), ensure it is re-applied after rebuild
      try {
        if (!lastLiveRef.current && lastLiveDailyRef.current) {
          const liveDaily = lastLiveDailyRef.current;
          const dateStartTs = Number(liveDaily.time);
          if (Number.isFinite(dateStartTs)) {
            const mappedArr = Array.isArray(mappedDataRef.current) ? mappedDataRef.current : [];
            const existing = mappedArr.find((m) => Number(m.time) === dateStartTs);
            let { open, high, low, close } = liveDaily;
            if (existing) {
              if (existing.open != null && Number(existing.open) !== 0) open = Number(existing.open);
              if (existing.high != null) high = Math.max(Number(existing.high), Number(high));
              if (existing.low != null) low = Math.min(Number(existing.low), Number(low));
            }
            candleSeriesRef.current.update({ time: dateStartTs, open: Number(open), high: Number(high), low: Number(low), close: Number(close) });
            try {
              const idx = mappedArr.findIndex(m => Number(m.time) === dateStartTs);
              const nextBar = { time: dateStartTs, open: Number(open), high: Number(high), low: Number(low), close: Number(close), volume: Number(liveDaily.volume || 0) };
              if (idx >= 0) mappedArr[idx] = { ...mappedArr[idx], ...nextBar };
              else mappedArr.push(nextBar);
              mappedArr.sort((a,b) => a.time - b.time);
              mappedDataRef.current = mappedArr;
            } catch {}
          }
        }
      } catch {}
    } else {
      candleSeriesRef.current = null;
    }

    // Apply reserved bottom margin to main (right) price scale so price pane shrinks
    try {
      const bottom = totalBottomMargin + pricePaddingBottom;
      if (candle) {
        candle.priceScale().applyOptions({ scaleMargins: { top: pricePaddingTop, bottom }, borderVisible: false });
      } else {
        chart.applyOptions({ rightPriceScale: { borderVisible: false, scaleMargins: { top: pricePaddingTop, bottom } } });
      }
      priceScaleMarginsRef.current = { top: pricePaddingTop, bottom };
    } catch {}

    // --- RSI & MACD Sub-Panes ---
    // Helper to create a dedicated price scale with margins
    const ensurePane = (id, topMargin, bottomMargin) => {
      try {
        const dummy = chart.addLineSeries({ priceScaleId: id, color: 'rgba(0,0,0,0)', lineWidth: 0, priceLineVisible: false, lastValueVisible: false });
        const ps = dummy.priceScale();
        ps.applyOptions({
          scaleMargins: { top: topMargin, bottom: bottomMargin },
          borderVisible: false,
        });
        // keep a reference so it's cleaned up
        lineSeriesRefs.current.push(dummy);
      } catch {}
    };

  // RSI
  if (enableRSI) {
  const rsiKey = 'rsi_rsi';
  const rsiColor = stylePrefs?.rsi?.line?.color || '#f59e0b';
  // Dedicated RSI pane stacked with dynamic margins
  const rsiM = paneMargins.rsi || { top: 0.58, bottom: 0.18 };
  ensurePane('rsi', rsiM.top, rsiM.bottom);

  const series = chart.addLineSeries({ color: rsiColor, lineWidth: stylePrefs?.rsi?.line?.width || 2, priceScaleId: 'rsi' });
      const points = (Array.isArray(data) && data.length && data.some(p => typeof p[rsiKey] !== 'undefined'))
        ? data.map(p => ({ time: Math.floor(parseUTCDate(p.date).getTime() / 1000), value: Number(p[rsiKey]) }))
            .filter(p => Number.isFinite(p.time) && Number.isFinite(p.value))
            .sort((a, b) => a.time - b.time)
        : (indicators[rsiKey] || []).map(p => ({ time: Math.floor(parseUTCDate(p.date).getTime() / 1000), value: Number(p.value) }))
            .filter(p => Number.isFinite(p.time) && Number.isFinite(p.value))
            .sort((a, b) => a.time - b.time);
      if (points.length) series.setData(points);
      lineSeriesRefs.current.push(series);

      // Draw 30/70 levels as dashed guide lines
      const level = (v) => {
        const guideColor = stylePrefs?.rsi?.guide?.color || (isDark ? 'rgba(148,163,184,0.7)' : 'rgba(100,116,139,0.7)');
        const ls = chart.addLineSeries({ color: guideColor, lineStyle: LineStyle.Dashed, lineWidth: stylePrefs?.rsi?.guide?.width || 1, priceScaleId: 'rsi' });
        const span = mapped.length ? [mapped[0].time, mapped[mapped.length - 1].time] : [];
        if (span.length) ls.setData([{ time: span[0], value: v }, { time: span[1], value: v }]);
        lineSeriesRefs.current.push(ls);
      };
      level(30);
      level(70);
    }

  // MACD
  if (enableMACD) {
  // Dedicated MACD pane stacked with dynamic margins
  const macdM = paneMargins.macd || { top: 0.35, bottom: 0.43 };
  ensurePane('macd', macdM.top, macdM.bottom);

  const mkLine = (color, width=2) => chart.addLineSeries({ color, lineWidth: width, priceScaleId: 'macd' });

      const macdKeys = {
        macd: 'macd_macd_line',
        signal: 'macd_signal_line',
        slope: 'macd_macd_slope',
        hist: 'macd_histogram',
      };

      const buildPoints = (key) => {
        const hasField = Array.isArray(data) && data.length && data.some(p => typeof p[key] !== 'undefined');
        const arr = hasField
          ? data.map(p => ({ time: Math.floor(parseUTCDate(p.date).getTime() / 1000), value: Number(p[key]) }))
          : (indicators[key] || []).map(p => ({ time: Math.floor(parseUTCDate(p.date).getTime() / 1000), value: Number(p.value) }));
        return arr.filter(p => Number.isFinite(p.time) && Number.isFinite(p.value)).sort((a, b) => a.time - b.time);
      };

      // MACD and Signal lines
  const macdSeries = mkLine(stylePrefs?.macd?.macdLine?.color || '#3b82f6', stylePrefs?.macd?.macdLine?.width || 2);
  const signalSeries = mkLine(stylePrefs?.macd?.signalLine?.color || '#ef4444', stylePrefs?.macd?.signalLine?.width || 2);
      const macdPts = buildPoints(macdKeys.macd);
      const sigPts = buildPoints(macdKeys.signal);
      if (macdPts.length) macdSeries.setData(macdPts);
      if (sigPts.length) signalSeries.setData(sigPts);
      lineSeriesRefs.current.push(macdSeries, signalSeries);

      // Histogram as baseline histogram around 0
  const histSeries = chart.addHistogramSeries({ priceScaleId: 'macd', base: 0 });
  const up = stylePrefs?.macd?.histUp || { color: 'rgba(16,185,129,0.6)' };
  const down = stylePrefs?.macd?.histDown || { color: 'rgba(239,68,68,0.6)' };
  const histPts = buildPoints(macdKeys.hist).map(p => ({ ...p, color: p.value >= 0 ? up.color : down.color }));
      if (histPts.length) histSeries.setData(histPts);
      lineSeriesRefs.current.push(histSeries);

      // Zero line
  const zeroColor = stylePrefs?.macd?.zeroLine?.color || (isDark ? 'rgba(148,163,184,0.7)' : 'rgba(100,116,139,0.7)');
  const zero = chart.addLineSeries({ color: zeroColor, lineStyle: LineStyle.Dashed, lineWidth: stylePrefs?.macd?.zeroLine?.width || 1, priceScaleId: 'macd' });
      const span = mapped.length ? [mapped[0].time, mapped[mapped.length - 1].time] : [];
      if (span.length) zero.setData([{ time: span[0], value: 0 }, { time: span[1], value: 0 }]);
      lineSeriesRefs.current.push(zero);
    }

    // ADX (+DI / -DI) in its own pane [0-100]
    if (enableADX) {
      const adxM = paneMargins.adx || { top: 0.12, bottom: 0.66 };
      ensurePane('adx', adxM.top, adxM.bottom);

  const adxColor = stylePrefs?.adx?.adx?.color || '#f59e0b';
  const plusColor = stylePrefs?.adx?.plus?.color || '#16a34a';
  const minusColor = stylePrefs?.adx?.minus?.color || '#dc2626';

      const buildPts = (fieldKey) => (
        (Array.isArray(data) ? data : [])
          .filter(d => d && d.date && (d[fieldKey] != null))
          .map(d => ({ time: Math.floor(parseUTCDate(d.date).getTime() / 1000), value: Number(d[fieldKey]) }))
          .filter(p => Number.isFinite(p.time) && Number.isFinite(p.value))
          .sort((a, b) => a.time - b.time)
      );

      const adxPts = buildPts('adx_adx');
      const plusPts = buildPts('adx_plus_di');
      const minusPts = buildPts('adx_minus_di');

      if (adxPts.length + plusPts.length + minusPts.length > 0) {
        const adxSeries = chart.addLineSeries({
          color: adxColor,
          lineWidth: stylePrefs?.adx?.adx?.width || 2,
          priceScaleId: 'adx',
          lastValueVisible: true,
          priceLineVisible: false,
        });
        const plusSeries = chart.addLineSeries({
          color: plusColor,
          lineWidth: stylePrefs?.adx?.plus?.width || 1.5,
          priceScaleId: 'adx',
          lastValueVisible: true,
          priceLineVisible: false,
        });
        const minusSeries = chart.addLineSeries({
          color: minusColor,
          lineWidth: stylePrefs?.adx?.minus?.width || 1.5,
          priceScaleId: 'adx',
          lastValueVisible: true,
          priceLineVisible: false,
        });

        if (adxPts.length) adxSeries.setData(adxPts);
        if (plusPts.length) plusSeries.setData(plusPts);
        if (minusPts.length) minusSeries.setData(minusPts);
        lineSeriesRefs.current.push(adxSeries, plusSeries, minusSeries);

        // Horizontal guide at 25 across visible span
        const span = mapped.length ? [mapped[0].time, mapped[mapped.length - 1].time] : [];
        if (span.length) {
          const guideColor = stylePrefs?.adx?.guide25?.color || (isDark ? 'rgba(148,163,184,0.7)' : 'rgba(100,116,139,0.7)');
          const guide = chart.addLineSeries({ color: guideColor, lineStyle: LineStyle.Dashed, lineWidth: stylePrefs?.adx?.guide25?.width || 1, priceScaleId: 'adx', lastValueVisible: false, priceLineVisible: false });
          guide.setData([{ time: span[0], value: 25 }, { time: span[1], value: 25 }]);
          lineSeriesRefs.current.push(guide);
        }
      }
    }

    // Predictions overlay: reserve horizontal space but only show on hover
    // Clear any existing prediction series
    if (predictionSeriesRef.current) {
      try { chartRef.current.removeSeries(predictionSeriesRef.current); } catch {}
      predictionSeriesRef.current = null;
    }
    
    let latestPrediction = null;
    let allPredictions = [];
    
    if (predictions && Array.isArray(predictions.docs) && predictions.docs.length) {
      // Sort docs by date to find the latest
      const sortedDocs = [...predictions.docs].sort((a, b) => {
        const aDate = a.date ? new Date(a.date) : new Date(0);
        const bDate = b.date ? new Date(b.date) : new Date(0);
        return bDate - aDate; // Most recent first
      });
      
      // Latest prediction for hover functionality
      latestPrediction = sortedDocs[0];
      
      // All predictions for hover functionality
      allPredictions = sortedDocs;
      
      // Reserve horizontal space without displaying the line: add invisible placeholder series
      // This prevents jitter when hovering from historical to predictions
      if (latestPrediction && Array.isArray(latestPrediction.preds) && latestPrediction.preds.length) {
        const predSeries = chartRef.current.addLineSeries({ 
          color: 'rgba(0,0,0,0)', // fully transparent
          lineWidth: 0,
          pointMarkersVisible: false,
          lastValueVisible: false,
          priceLineVisible: false,
        });

        // Build and sort prediction points for space reservation
        const predictionPoints = latestPrediction.preds.map(p => {
          const parsedDate = parseUTCDate(p.date);
          const timestamp = Math.floor(parsedDate.getTime() / 1000);
          return { time: timestamp, value: Number(p.value) };
        }).filter(p => Number.isFinite(p.time) && p.time > 0 && Number.isFinite(p.value))
          .sort((a, b) => a.time - b.time);

        // Remove duplicates by timestamp
        const uniquePredictionPoints = predictionPoints.filter((point, index, array) => 
          index === 0 || point.time !== array[index - 1].time
        );

        // Last historical data point (to potentially connect from)
        const lastHistoricalPoint = mapped.length > 0 ? mapped[mapped.length - 1] : null;

        let pts = uniquePredictionPoints;
        if (lastHistoricalPoint && uniquePredictionPoints.length > 0) {
          const firstPred = uniquePredictionPoints[0];
          if (firstPred.time >= lastHistoricalPoint.time) {
            // Safe to connect: last historical time is <= first prediction time
            if (firstPred.time !== lastHistoricalPoint.time) {
              pts = [
                { time: lastHistoricalPoint.time, value: lastHistoricalPoint.close },
                ...uniquePredictionPoints,
              ];
            }
          } else {
            // Predictions are before last historical candle; do not connect and drop non-future points
            pts = uniquePredictionPoints.filter(p => p.time > lastHistoricalPoint.time);
          }
        }

        // Final safety: sort ascending and de-duplicate
        pts = pts.sort((a, b) => a.time - b.time)
                 .filter((p, i, arr) => i === 0 || p.time !== arr[i - 1].time);

        if (pts.length > 0) {
          predSeries.setData(pts);
          predictionSeriesRef.current = predSeries;
        }
      }
    }

    // Top-left forward trend label removed (will be shown in toolbar)

    // Hover and persistent info box
    const hoverDiv = document.createElement('div');
    hoverDiv.className = 'absolute pointer-events-none text-xs rounded-md shadow px-2 py-1';
    hoverDiv.style.zIndex = 10;
    hoverDiv.style.display = 'none';
    hoverDiv.style.background = isDark ? 'rgba(31,41,55,0.6)' : 'rgba(255,255,255,0.6)';
    hoverDiv.style.border = isDark ? '1px solid #374151' : '1px solid #e5e7eb';
    // Single-line tooltip content
    hoverDiv.style.whiteSpace = 'nowrap';
    containerRef.current.style.position = 'relative';
    containerRef.current.appendChild(hoverDiv);
    hoverDivRef.current = hoverDiv;

    const fmt = (d) => {
      const dateStr = new Intl.DateTimeFormat('en-US', { month: 'short', day: '2-digit', timeZone: 'UTC' }).format(d);
      const timeStr = new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' }).format(d);
      return `${dateStr}, ${timeStr}`;
    };
    
    const renderInfo = (ts, allPredictions = []) => {
      if (!Array.isArray(allPredictions) || !allPredictions.length) return null;
      const d = new Date(ts * 1000);

      // Find the most recent prediction document whose doc-level date matches this hovered date
      let bestPrediction = null;
      let bestTieBreaker = new Date(0);

      for (const pred of allPredictions) {
        if (!pred) continue;
        const docDate = parseUTCDate(pred.date || pred.created_at || (pred.preds && pred.preds[0] && pred.preds[0].date));
        if (!(docDate instanceof Date) || isNaN(docDate.getTime())) continue;
        const sameDay = docDate.getUTCFullYear() === d.getUTCFullYear() &&
                        docDate.getUTCMonth() === d.getUTCMonth() &&
                        docDate.getUTCDate() === d.getUTCDate();
        if (!sameDay) continue;

        const tieBreaker = parseUTCDate(pred.created_at || pred.date);
        if (tieBreaker > bestTieBreaker) {
          bestPrediction = pred;
          bestTieBreaker = tieBreaker;
        }
      }

      if (!bestPrediction) return null;

      const prediction = bestPrediction;
      const lines = [];
      if (prediction.forward_trend) lines.push(`Trend: ${prediction.forward_trend}`);
      if (typeof prediction.confidence === 'number') lines.push(`Confidence: ${(prediction.confidence * 100).toFixed(0)}%`);
      if (prediction.model) lines.push(`Model: ${prediction.model}`);

      if (prediction.preds && prediction.preds.length > 0) {
        const predValues = prediction.preds.slice(0, 3).map(p => Number(p.value).toFixed(2)).join(', ');
        lines.push(`Predictions: ${predValues}`);
      }

      return { text: `${fmt(d)}\n${lines.join('\n')}`, prediction, match: null };
    };  // Persistent latest prediction panel removed per request

    // Shade future region (beyond the last candle time)
    if (futureShadeRef.current && futureShadeRef.current.parentNode) {
      try { futureShadeRef.current.parentNode.removeChild(futureShadeRef.current); } catch {}
      futureShadeRef.current = null;
    }
  const lastCandleTs = mapped.length ? mapped[mapped.length - 1].time : null;
  const timeToBar = new Map(mapped.map(p => [p.time, p]));
    if (lastCandleTs) {
      const shade = document.createElement('div');
      shade.style.position = 'absolute';
      shade.style.top = '0';
      shade.style.bottom = '0';
      shade.style.pointerEvents = 'none';
  shade.style.background = isDark ? 'rgba(75,85,99,0.35)' : 'rgba(229,231,235,0.6)'; // dark gray vs light gray
  shade.style.zIndex = 1; // below tooltips/info, above chart canvas
      containerRef.current.appendChild(shade);
      futureShadeRef.current = shade;

      const updateShade = () => {
        try {
          const timeScale = chart.timeScale();
          const width = containerRef.current.clientWidth || 0;
          const x = timeScale.timeToCoordinate(lastCandleTs);
          if (typeof x === 'number') {
            shade.style.left = `${Math.max(0, Math.floor(x))}px`;
            shade.style.right = '0px';
          } else {
            // If coordinate is not ready yet, hide temporarily
            shade.style.left = `${width}px`;
            shade.style.right = '0px';
          }
        } catch {}
      };
      // initial and on size changes
      updateShade();
      const ro = new ResizeObserver(() => updateShade());
      ro.observe(containerRef.current);
      const shadeHandler = () => updateShade();
      shadeHandlerRef.current = shadeHandler;
      chart.timeScale().subscribeVisibleTimeRangeChange(shadeHandler);
      // store cleanup
      shade._cleanup = () => {
        try { ro.disconnect(); } catch {}
        try {
          if (shadeHandlerRef.current) {
            chart.timeScale().unsubscribeVisibleTimeRangeChange(shadeHandlerRef.current);
            shadeHandlerRef.current = null;
          }
        } catch {}
      };
    }

    const crosshairHandler = (param) => {
      if (isHandlingRef.current) return;
      isHandlingRef.current = true;
      try {
        if (!param?.time || !hoverDivRef.current) {
          if (hoverDivRef.current) hoverDivRef.current.style.display = 'none';
          if (hoverPredictionSeriesRef.current) hoverPredictionSeriesRef.current.setData([]);
          return;
        }
        const ts = toTs(param.time);
        if (!Number.isFinite(ts)) {
          hoverDivRef.current.style.display = 'none';
          if (hoverPredictionSeriesRef.current) hoverPredictionSeriesRef.current.setData([]);
          return;
        }
        // OHLC display for hovered bar
        let bar = null;
        try {
          if (param.seriesData && typeof param.seriesData.get === 'function') {
            bar = param.seriesData.get(candle) || null;
          }
        } catch {}
        if (!bar) {
          bar = timeToBar.get(ts) || null;
        }
        const linesOHLC = [];
        if (bar && typeof bar.open === 'number') {
          linesOHLC.push(
            `O:${Number(bar.open).toFixed(2)}  H:${Number(bar.high).toFixed(2)}  L:${Number(bar.low).toFixed(2)}  C:${Number(bar.close).toFixed(2)}`
          );
        }
        // Divergence info for hovered bar if enabled and visible
        const linesDiv = [];
        try {
          const mappedBar = timeToBar.get(ts);
          // Check if the hovered timestamp is within viewport horizontally to mirror label visibility
          const x = chartRef.current?.timeScale()?.timeToCoordinate(ts);
          const width = containerRef.current?.clientWidth || 0;
          const within = typeof x === 'number' ? (x >= 0 && x <= width) : true; // if unknown, allow
          if (mappedBar && within) {
            if (enableHiddenDivRef.current) {
              const hb = Number(mappedBar.div_hid_bullish);
              const hr = Number(mappedBar.div_hid_bearish);
              const parts = [];
              if (Number.isFinite(hb) && Math.abs(hb) > 0) parts.push(`+${Math.abs(hb).toFixed(1)}`);
              if (Number.isFinite(hr) && Math.abs(hr) > 0) parts.push(`-${Math.abs(hr).toFixed(1)}`);
              if (parts.length) linesDiv.push(`Adp. Hidden Div: ${parts.join(' ')}`);
            }
            if (enableRegularDivRef.current) {
              const rb = Number(mappedBar.div_reg_bullish);
              const rr = Number(mappedBar.div_reg_bearish);
              const parts = [];
              if (Number.isFinite(rb) && Math.abs(rb) > 0) parts.push(`+${Math.abs(rb).toFixed(1)}`);
              if (Number.isFinite(rr) && Math.abs(rr) > 0) parts.push(`-${Math.abs(rr).toFixed(1)}`);
              if (parts.length) linesDiv.push(`Adp. Regular Div: ${parts.join(' ')}`);
            }
            if (enableKnoxvilleDivRef.current) {
              const kv = Number(mappedBar.knox_div_divergence_signal);
              if (Number.isFinite(kv) && kv !== 0) {
                const sign = kv > 0 ? '+' : '-';
                const magnitude = Math.abs(kv).toFixed(2);
                linesDiv.push(`Knoxville Div: ${sign}${magnitude}`);
              }
            }
            if (enableDvlHiddenDivRef.current) {
              const hb = Number(mappedBar.dver_hidden_bullish);
              const hr = Number(mappedBar.dver_hidden_bearish);
              const parts = [];
              if (Number.isFinite(hb) && Math.abs(hb) > 0) parts.push(`+${Math.abs(hb).toFixed(1)}`);
              if (Number.isFinite(hr) && Math.abs(hr) > 0) parts.push(`-${Math.abs(hr).toFixed(1)}`);
              if (parts.length) linesDiv.push(`Hidden Div: ${parts.join(' ')}`);
            }
            if (enableDvlRegularDivRef.current) {
              const rb = Number(mappedBar.dver_regular_bullish);
              const rr = Number(mappedBar.dver_regular_bearish);
              const parts = [];
              if (Number.isFinite(rb) && Math.abs(rb) > 0) parts.push(`+${Math.abs(rb).toFixed(1)}`);
              if (Number.isFinite(rr) && Math.abs(rr) > 0) parts.push(`-${Math.abs(rr).toFixed(1)}`);
              if (parts.length) linesDiv.push(`Regular Div: ${parts.join(' ')}`);
            }
          }
        } catch {}
        
        let info = renderInfo(ts, allPredictions);
        // If renderInfo didn't find a doc-level prediction for this hovered timestamp,
        // try to find a matching prediction point (pred.preds) at the exact timestamp
        // so hovering a prediction line/point will show its value.
        if ((!info || !info.prediction) && Array.isArray(allPredictions) && allPredictions.length) {
          outer: for (const predDoc of allPredictions) {
            if (!predDoc || !Array.isArray(predDoc.preds)) continue;
            for (const pp of predDoc.preds) {
              try {
                const parsed = parseUTCDate(pp.date);
                const pTs = Math.floor(parsed.getTime() / 1000);
                if (Number.isFinite(pTs) && pTs === ts) {
                  // Found an exact prediction point at the hovered timestamp
                  info = info || { text: '', prediction: predDoc, match: null };
                  info.prediction = predDoc;
                  info.match = { value: Number(pp.value), time: pTs, point: pp };
                  break outer;
                }
              } catch {}
            }
          }
        }

        if (info && info.prediction) {
          const pred = info.prediction;
          if (pred && Array.isArray(pred.preds) && pred.preds.length) {
            // Get all prediction points for this prediction
            const allPredictionPoints = pred.preds.map(p => {
              const parsedDate = parseUTCDate(p.date);
              const timestamp = Math.floor(parsedDate.getTime() / 1000);
              return {
                time: timestamp,
                value: Number(p.value),
                date: p.date
              };
            }).filter(p => !isNaN(p.time) && p.time > 0 && !isNaN(p.value))
              .sort((a, b) => a.time - b.time);

            // Find predictions that start AFTER the hovered date (ts)
            const futurePredictions = allPredictionPoints.filter(p => p.time > ts);

            if (futurePredictions.length > 0) {
              // Remove duplicates from future predictions
              const uniqueFuturePredictions = futurePredictions.filter((point, index, array) => 
                index === 0 || point.time !== array[index - 1].time
              );

              const barForConnection = timeToBar.get(ts);
              let pts = [...uniqueFuturePredictions];

              // Connect from the hovered date's close price to the first future prediction
              if (barForConnection) {
                pts.unshift({
                  time: barForConnection.time,
                  value: barForConnection.close
                });
              }

              if (hoverPredictionSeriesRef.current && pts.length > 0) {
                hoverPredictionSeriesRef.current.setData(pts);
              }
            } else {
              // No future predictions available
              if (hoverPredictionSeriesRef.current) {
                hoverPredictionSeriesRef.current.setData([]);
              }
            }
          }
        } else {
          if (hoverPredictionSeriesRef.current) {
            hoverPredictionSeriesRef.current.setData([]);
          }
        }

        const dateStr = fmt(new Date(ts * 1000));
        // OHLC string
        const ohlcStr = (linesOHLC.length ? linesOHLC[0] : null);
        // Predictions single-line segment
        let predSegment = '';
        if (info && info.prediction) {
          const pred = info.prediction;
          const modelName = pred.model || (predictions && predictions.model) || (typeof selectedModel !== 'undefined' ? selectedModel : '') || 'Model';
          let predsVals = '';
          if (Array.isArray(pred.preds) && pred.preds.length) {
            predsVals = pred.preds.slice(0, 3).map(p => Number(p.value).toFixed(2)).join(', ');
          }
          const trendTxt = pred.forward_trend ? ` (${pred.forward_trend})` : '';
          if (predsVals) {
            predSegment = ` | Predictions (${modelName}): ${predsVals}${trendTxt}`;
          } else if (pred.forward_trend || modelName) {
            // Show segment even if preds missing, to include model/trend context
            predSegment = ` | Predictions (${modelName}): ${trendTxt}`;
          }
          // If we found an exact hovered prediction point, append its value so the tooltip shows the hovered point
          if (info.match && typeof info.match.value === 'number') {
            try {
              const mv = Number(info.match.value).toFixed(2);
              predSegment += ` | Hovered Prediction: ${mv}`;
            } catch {}
          }
        }
        // Divergences segment
        const divSegment = linesDiv.length ? ` | ${linesDiv.join(' | ')}` : '';
        // Final single-line tooltip
        const singleLine = `${dateStr}${ohlcStr ? ` ${ohlcStr}` : ''}${predSegment}${divSegment}`.trim();
        if (!singleLine) {
          hoverDivRef.current.style.display = 'none';
          if (hoverMarkersSeriesRef.current) hoverMarkersSeriesRef.current.setMarkers([]);
          return;
        }
        if (info && info.match && hoverMarkersSeriesRef.current) {
          hoverMarkersSeriesRef.current.setMarkers([{
            time: ts,
            position: 'inBar',
            color: '#f59e0b',
            shape: 'circle',
            text: Number(info.match.value).toFixed(2)
          }]);
        } else if (hoverMarkersSeriesRef.current) {
          hoverMarkersSeriesRef.current.setMarkers([]);
        }
        // Build rich HTML for tooltip so we can color the trend text
        try {
          const escapeHtml = (s) => String(s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');

          const trendSpan = (prediction) => {
            if (!prediction || !prediction.forward_trend) return '';
            const t = String(prediction.forward_trend || '');
            const tl = t.toLowerCase();
            const isUp = tl.includes('up') || tl.includes('bull') || tl.includes('+');
            const isDown = tl.includes('down') || tl.includes('bear') || tl.includes('-');
            const color = isUp ? (isDark ? '#86efac' : '#16a34a') : (isDown ? (isDark ? '#fecaca' : '#dc2626') : (isDark ? '#e5e7eb' : '#374151'));
            return `<span style="font-weight:700;color:${color}">${escapeHtml(t)}</span>`;
          };

          let html = escapeHtml(dateStr);
          if (ohlcStr) html += ' ' + escapeHtml(ohlcStr);
          if (info && info.prediction) {
            const pred = info.prediction;
            const modelName = pred.model || (predictions && predictions.model) || (typeof selectedModel !== 'undefined' ? selectedModel : '') || 'Model';
            let predsVals = '';
            if (Array.isArray(pred.preds) && pred.preds.length) {
              predsVals = pred.preds.slice(0, 3).map(p => Number(p.value).toFixed(2)).join(', ');
            }
            if (predsVals) {
              html += ' | Predictions (' + escapeHtml(modelName) + '): ' + escapeHtml(predsVals);
              if (pred.forward_trend) html += ' (' + trendSpan(pred) + ')';
            } else if (pred.forward_trend || modelName) {
              html += ' | Predictions (' + escapeHtml(modelName) + '): ' + (pred.forward_trend ? (' (' + trendSpan(pred) + ')') : '');
            }
            // If hovering a specific prediction point, include its value prominently
            if (info.match && typeof info.match.value === 'number') {
              html += ' | Hovered Prediction: <span style="font-weight:700;color:' + (isDark ? '#f59e0b' : '#b45309') + '">' + escapeHtml(Number(info.match.value).toFixed(2)) + '</span>';
            }
          }
          if (divSegment) html += ' ' + escapeHtml(divSegment);

          hoverDivRef.current.innerHTML = html;
        } catch (e) {
          // Fallback to plain text if anything goes wrong
          hoverDivRef.current.textContent = singleLine;
        }
        // Position tooltip at the top-left so it doesn't cover the time axis or volume pane
        hoverDivRef.current.style.left = '8px';
        hoverDivRef.current.style.right = 'auto';
        hoverDivRef.current.style.bottom = 'auto';
        hoverDivRef.current.style.top = '8px';
        hoverDivRef.current.style.display = 'block';
      } finally {
        isHandlingRef.current = false;
      }
    };
  chart.subscribeCrosshairMove(crosshairHandler);
  crosshairHandlerRef.current = crosshairHandler;

    // Signal overlays that chart was (re)created
    try { setChartGen((v) => v + 1); } catch {}

    return () => {
      // Cleanup: remove series from current chart instance before removing chart
      if (chartRef.current) {
        try {
          if (predictionSeriesRef.current) {
            try { chartRef.current.removeSeries(predictionSeriesRef.current); } catch {}
            predictionSeriesRef.current = null;
          }
          if (hoverPredictionSeriesRef.current) {
            try { chartRef.current.removeSeries(hoverPredictionSeriesRef.current); } catch {}
            hoverPredictionSeriesRef.current = null;
          }
          if (hoverMarkersSeriesRef.current) {
            try { chartRef.current.removeSeries(hoverMarkersSeriesRef.current); } catch {}
            hoverMarkersSeriesRef.current = null;
          }
          if (Array.isArray(lineSeriesRefs.current) && lineSeriesRefs.current.length) {
            lineSeriesRefs.current.forEach(s => { try { if (s) chartRef.current.removeSeries(s); } catch {} });
            lineSeriesRefs.current = [];
          }
          if (candleSeriesRef.current) {
            try { chartRef.current.removeSeries(candleSeriesRef.current); } catch {}
            candleSeriesRef.current = null;
          }
        } finally {
          try { chartRef.current.remove(); } catch {}
          chartRef.current = null;
        }
      }
      if (crosshairHandlerRef.current && chartRef.current) {
        try { chartRef.current.unsubscribeCrosshairMove(crosshairHandlerRef.current); } catch {}
        crosshairHandlerRef.current = null;
      }
      if (infoBoxRef.current && typeof infoBoxRef.current._divergenceCleanup === 'function') {
        try { infoBoxRef.current._divergenceCleanup(); } catch {}
      }
      if (hoverDivRef.current && hoverDivRef.current.parentNode) {
        try { hoverDivRef.current.parentNode.removeChild(hoverDivRef.current); } catch {}
        hoverDivRef.current = null;
      }
      if (trendDivRef.current && trendDivRef.current.parentNode) {
        try { trendDivRef.current.parentNode.removeChild(trendDivRef.current); } catch {}
        trendDivRef.current = null;
      }
      if (infoBoxRef.current && infoBoxRef.current.parentNode) {
        try { infoBoxRef.current.parentNode.removeChild(infoBoxRef.current); } catch {}
        infoBoxRef.current = null;
      }
      if (tradeOverlayRef.current && tradeOverlayRef.current.parentNode) {
        try { tradeOverlayRef.current.parentNode.removeChild(tradeOverlayRef.current); } catch {}
        tradeOverlayRef.current = null;
      }
      if (cloudOverlayRef.current && cloudOverlayRef.current.parentNode) {
        try { cloudOverlayRef.current.parentNode.removeChild(cloudOverlayRef.current); } catch {}
        cloudOverlayRef.current = null;
      }
      if (futureShadeRef.current && futureShadeRef.current.parentNode) {
        try { if (futureShadeRef.current._cleanup) futureShadeRef.current._cleanup(); } catch {}
        try { futureShadeRef.current.parentNode.removeChild(futureShadeRef.current); } catch {}
        futureShadeRef.current = null;
      }
    };
  }, [data, indicators, predictions, theme, height, showCandle, showLine, showVolume, smaPeriods, emaPeriods, kamaPeriods, enableBollinger, enableIchimoku, enableRSIBands, enableMACD, enableRSI, enableADX, stylePrefs]);

  // Dedicated effect to render/update divergence labels without recreating the chart
  useEffect(() => {
    const chart = chartRef.current;
    const container = containerRef.current;
    const mapped = Array.isArray(mappedDataRef.current) ? mappedDataRef.current : [];
    if (!chart || !container) return;

    // Cleanup any previous divergence overlay and hidden series
    const cleanupPrev = () => {
      if (infoBoxRef.current && infoBoxRef.current.parentNode) {
        try { infoBoxRef.current.parentNode.removeChild(infoBoxRef.current); } catch {}
        infoBoxRef.current = null;
      }
      if (divergenceCoordSeriesRef.current && chartRef.current) {
        try { chartRef.current.removeSeries(divergenceCoordSeriesRef.current); } catch {}
        divergenceCoordSeriesRef.current = null;
      }
    };

    const enabled = enableHiddenDiv || enableRegularDiv || enableKnoxvilleDiv || enableDvlHiddenDiv || enableDvlRegularDiv;
    if (!enabled || !mapped.length) { cleanupPrev(); return; }

    // Colors consistent with main effect
    const isDark = theme === 'dark';
    const colors = isDark
      ? {
          // Dark theme: even lighter tints for hidden, mid tones for regular
          hidBull: stylePrefs?.div?.hidBull?.color || '#dcfce7',
          hidBear: stylePrefs?.div?.hidBear?.color || '#fee2e2',
          regBull: stylePrefs?.div?.regBull?.color || '#22c55e',
          regBear: stylePrefs?.div?.regBear?.color || '#ef4444',
          knox: stylePrefs?.div?.knox?.color || '#3b82f6',
          // DVL (divergence levels) – purple-ish tints for visual distinction
          dvlHidBull: stylePrefs?.dvl?.hidBull?.color || '#83ae29',
          dvlHidBear: stylePrefs?.dvl?.hidBear?.color || '#ea6710',
          dvlRegBull: stylePrefs?.dvl?.regBull?.color || '#07f2cb',
          dvlRegBear: stylePrefs?.dvl?.regBear?.color || '#fa0000',
        }
      : {
          // Light theme
          hidBull: stylePrefs?.div?.hidBull?.color || '#16a34a',
          hidBear: stylePrefs?.div?.hidBear?.color || '#ef4444',
          regBull: stylePrefs?.div?.regBull?.color || '#15803d',
          regBear: stylePrefs?.div?.regBear?.color || '#b91c1c',
          knox: stylePrefs?.div?.knox?.color || '#2563eb',
          dvlHidBull: stylePrefs?.dvl?.hidBull?.color || '#83ae29',
          dvlHidBear: stylePrefs?.dvl?.hidBear?.color || '#ea6710',
          dvlRegBull: stylePrefs?.dvl?.regBull?.color || '#07f2cb',
          dvlRegBear: stylePrefs?.dvl?.regBear?.color || '#fa0000',
        };

    // Ensure container clips
    try { container.style.overflow = 'hidden'; } catch {}

    // Choose a series for priceToCoordinate
    const seriesForCoords = candleSeriesRef.current || (divergenceCoordSeriesRef.current = chart.addLineSeries({ color: 'rgba(0,0,0,0)', lineWidth: 0 }));

    // Create overlay container
    const labelContainer = document.createElement('div');
    labelContainer.style.position = 'absolute';
    labelContainer.style.left = '0';
    labelContainer.style.top = '0';
    labelContainer.style.bottom = '0';
    labelContainer.style.right = '0';
    labelContainer.style.pointerEvents = 'none';
    labelContainer.style.zIndex = 5;
    labelContainer.style.overflow = 'hidden';
    container.appendChild(labelContainer);
    infoBoxRef.current = labelContainer;

    const makeLabel = (x, y, text, color) => {
      const span = document.createElement('span');
      span.textContent = text;
      span.style.position = 'absolute';
      span.style.left = `${Math.round(x + 6)}px`;
      span.style.top = `${Math.round(y - 9)}px`;
      span.style.fontSize = '10px';
      span.style.fontWeight = '600';
      span.style.color = color;
      span.style.background = 'transparent';
      span.style.padding = '0 2px';
      span.style.borderRadius = '2px';
      return span;
    };

    const priceToCoord = (p) => { try { return seriesForCoords.priceToCoordinate(p); } catch { return null; } };
    const timeToCoord = (t) => { try { return chart.timeScale().timeToCoordinate(t); } catch { return null; } };

    // Build index map for bars-back lookup (used by Knox dotted lines)
    const barIndexMap = new Map(mapped.map((b, i) => [b.time, i]));

    // SVG layer for Knox bars-back dotted lines (below divergence labels)
    const svgNS = 'http://www.w3.org/2000/svg';
    const knoxLinesSvg = document.createElementNS(svgNS, 'svg');
    knoxLinesSvg.style.position = 'absolute';
    knoxLinesSvg.style.left = '0';
    knoxLinesSvg.style.top = '0';
    knoxLinesSvg.style.right = '0';
    knoxLinesSvg.style.bottom = '0';
    knoxLinesSvg.style.width = '100%';
    knoxLinesSvg.style.height = '100%';
    knoxLinesSvg.style.pointerEvents = 'none';
    knoxLinesSvg.style.zIndex = 4; // below divergence labels (5)
    knoxLinesSvg.style.overflow = 'visible';
    container.appendChild(knoxLinesSvg);

    // Dash patterns for up to 5 distinct N values (sorted ascending)
    const DASH_PATTERNS = ['4,3', '8,3', '2,3', '8,3,2,3', '4,3,8,3'];
    // Knox color (same as label chip)
    const knoxColor = stylePrefs?.div?.knox?.color || colors.knox;

    const absFmt = (v, isInt = false) => {
  if (v == null || !Number.isFinite(Number(v))) return null;
  const n = Math.abs(Number(v));
  return isInt ? Math.round(n).toString() : n.toFixed(1);
    };

    let rafId = 0;
    let scheduled = false;
    const scheduleRender = () => {
      if (scheduled) return;
      scheduled = true;
      rafId = requestAnimationFrame(() => { scheduled = false; render(); });
    };

    const render = () => {
      try { labelContainer.innerHTML = ''; } catch {}
      try { while (knoxLinesSvg.firstChild) knoxLinesSvg.removeChild(knoxLinesSvg.firstChild); } catch {}
      const width = container.clientWidth || 0;
      const height_px = container.clientHeight || 0;
      knoxLinesSvg.setAttribute('width', String(Math.max(1, width)));
      knoxLinesSvg.setAttribute('height', String(Math.max(1, height_px)));
      let maxBearItems = 0; // track the largest number of bearish chips stacked above highs in view
      for (const bar of mapped) {
        const x = timeToCoord(bar.time);
        if (typeof x !== 'number') continue;
        if (!(x >= 0 && x <= width)) continue;

        // Collect visible divergences for this bar
        const values = [];
        if (enableHiddenDiv) {
          const bull = Number(bar.div_hid_bullish);
          const bear = Number(bar.div_hid_bearish);
          if (Number.isFinite(bull) && Math.abs(bull) > 0) values.push({ type: 'hidBull', up: true, text: absFmt(bull) });
          if (Number.isFinite(bear) && Math.abs(bear) > 0) values.push({ type: 'hidBear', up: false, text: absFmt(bear) });
        }
        if (enableRegularDiv) {
          const bull = Number(bar.div_reg_bullish);
          const bear = Number(bar.div_reg_bearish);
          if (Number.isFinite(bull) && Math.abs(bull) > 0) values.push({ type: 'regBull', up: true, text: absFmt(bull) });
          if (Number.isFinite(bear) && Math.abs(bear) > 0) values.push({ type: 'regBear', up: false, text: absFmt(bear) });
        }
        if (enableKnoxvilleDiv) {
          const val = Number(bar.knox_div_divergence_signal);
          if (Number.isFinite(val) && val !== 0) values.push({ type: 'knox', up: val > 0, text: absFmt(val, true) });
        }
        if (enableDvlHiddenDiv) {
          const bull = Number(bar.dver_hidden_bullish);
          const bear = Number(bar.dver_hidden_bearish);
          if (Number.isFinite(bull) && Math.abs(bull) > 0) values.push({ type: 'dvlHidBull', up: true, text: absFmt(bull) });
          if (Number.isFinite(bear) && Math.abs(bear) > 0) values.push({ type: 'dvlHidBear', up: false, text: absFmt(bear) });
        }
        if (enableDvlRegularDiv) {
          const bull = Number(bar.dver_regular_bullish);
          const bear = Number(bar.dver_regular_bearish);
          if (Number.isFinite(bull) && Math.abs(bull) > 0) values.push({ type: 'dvlRegBull', up: true, text: absFmt(bull) });
          if (Number.isFinite(bear) && Math.abs(bear) > 0) values.push({ type: 'dvlRegBear', up: false, text: absFmt(bear) });
        }
        if (!values.length) continue;

        // Split into bullish (below low) and bearish (above high); it's possible to have both
        const bullValues = values.filter(v => v.up === true);
        const bearValues = values.filter(v => v.up === false);
        const colorByType = { hidBull: colors.hidBull, hidBear: colors.hidBear, regBull: colors.regBull, regBear: colors.regBear, knox: colors.knox, dvlHidBull: colors.dvlHidBull, dvlHidBear: colors.dvlHidBear, dvlRegBull: colors.dvlRegBull, dvlRegBear: colors.dvlRegBear };
        const makeBox = (items, anchorY, placeBelow) => {
          if (!items.length) return null;
          // Wrapper anchors the reference line: top of wrapper is the anchor + gap (below) or anchor - gap (above)
          const wrapper = document.createElement('div');
          wrapper.style.position = 'absolute';
          wrapper.style.left = '0px';
          wrapper.style.top = '0px';
          wrapper.style.pointerEvents = 'none';
          // Create the box content inside the wrapper (no rotation)
          const box = document.createElement('div');
          box.style.position = 'absolute';
          box.style.left = '0px';
          box.style.top = '0px';
          box.style.pointerEvents = 'none';
          box.style.border = isDark ? '1px solid #374151' : '1px solid #e5e7eb';
          const boxBg = isDark ? 'rgba(31,41,55,0.92)' : 'rgba(255,255,255,0.92)';
          box.style.background = boxBg;
          box.style.borderRadius = '4px';
          box.style.padding = '1px 3px';
          box.style.display = 'flex';
          box.style.alignItems = 'center';
          box.style.gap = '6px';
          // If multiple divergences, stack vertically
          if (items.length > 1) {
            box.style.flexDirection = 'column';
            box.style.alignItems = 'center';
            box.style.gap = '2px';
          } else {
            box.style.flexDirection = 'row';
          }

          // Order per request:
          // Bearish (above high): Knoxville (top), DVL Regular, DVL Hidden, Regular (middle), Hidden (bottom)
          // Bullish (below low): Hidden (top), Regular, DVL Hidden, DVL Regular (middle), Knoxville (bottom)
          const order = placeBelow
            ? ['hidBull', 'regBull', 'dvlHidBull', 'dvlRegBull', 'knox']
            : ['knox', 'dvlRegBear', 'dvlHidBear', 'regBear', 'hidBear'];
          for (const key of order) {
            const item = items.find(v => v.type === key);
            if (!item) continue;
            const chip = document.createElement('span');
            chip.textContent = String(item.text);
            // Slightly increased font for numbers in divergence box
            chip.style.fontSize = '9px';
            chip.style.fontWeight = '700';
            chip.style.color = colorByType[item.type];
            // Opacity per request: hidden 40%, regular 60%, knoxville 80%
            const opacityByType = {
              hidBull: stylePrefs?.div?.hidBull?.alpha ?? (isDark ? 0.4 : 0.6),
              hidBear: stylePrefs?.div?.hidBear?.alpha ?? (isDark ? 0.4 : 0.6),
              regBull: stylePrefs?.div?.regBull?.alpha ?? 0.6,
              regBear: stylePrefs?.div?.regBear?.alpha ?? 0.6,
              knox: stylePrefs?.div?.knox?.alpha ?? 0.8,
              dvlHidBull: stylePrefs?.dvl?.hidBull?.alpha ?? 0.7,
              dvlHidBear: stylePrefs?.dvl?.hidBear?.alpha ?? 0.7,
              dvlRegBull: stylePrefs?.dvl?.regBull?.alpha ?? 0.9,
              dvlRegBear: stylePrefs?.dvl?.regBear?.alpha ?? 0.9,
            };
            const op = opacityByType[item.type];
            if (typeof op === 'number') chip.style.opacity = String(op);
            chip.style.whiteSpace = 'nowrap';
            box.appendChild(chip);
          }

          wrapper.appendChild(box);
          labelContainer.appendChild(wrapper);
          // Measure size and align the visual box rect to the desired anchor line (top for bullish, bottom for bearish)
          try {
            const containerRect = labelContainer.getBoundingClientRect();
            const rect = box.getBoundingClientRect();
            const gap = placeBelow ? 36 : 28; // larger separation so trade signals stay closer to price
            // Desired visual positions
            let targetLeft = Math.round(x - rect.width / 2);
            const maxLeft = Math.max(0, (container.clientWidth || 0) - Math.round(rect.width));
            if (targetLeft < 0) targetLeft = 0;
            if (targetLeft > maxLeft) targetLeft = maxLeft;
            const deltaLeft = rect.left - containerRect.left;
            const leftPx = targetLeft - deltaLeft;

            if (placeBelow) {
              const targetTop = Math.round(anchorY + gap);
              const deltaTop = rect.top - containerRect.top;
              const topPx = targetTop - deltaTop;
              wrapper.style.left = `${Math.round(leftPx)}px`;
              wrapper.style.top = `${Math.round(topPx)}px`;
            } else {
              const targetBottom = Math.round(anchorY - gap);
              const deltaBottom = rect.bottom - containerRect.top;
              const topPx = targetBottom - deltaBottom; // set wrapper so visual bottom equals targetBottom
              wrapper.style.left = `${Math.round(leftPx)}px`;
              wrapper.style.top = `${Math.round(topPx)}px`;
            }

            // Add a more prominent directional arrow (SVG with stroke)
            const aw = 14; // arrow base width
            const ah = 10; // arrow height
            const svgNS = 'http://www.w3.org/2000/svg';
            const arrowSvg = document.createElementNS(svgNS, 'svg');
            arrowSvg.setAttribute('width', String(aw));
            arrowSvg.setAttribute('height', String(ah));
            arrowSvg.style.position = 'absolute';
            arrowSvg.style.left = `${Math.round(rect.width / 2 - aw / 2)}px`;
            // Stroke color follows border for visibility
            const strokeColor = isDark ? '#374151' : '#9ca3af';
            if (placeBelow) {
              // Upwards pointing triangle above the box
              arrowSvg.style.top = `${-ah}px`;
              const poly = document.createElementNS(svgNS, 'polygon');
              poly.setAttribute('points', `0,${ah} ${aw/2},0 ${aw},${ah}`);
              poly.setAttribute('fill', boxBg);
              poly.setAttribute('stroke', strokeColor);
              poly.setAttribute('stroke-width', '1');
              arrowSvg.appendChild(poly);
            } else {
              // Downwards pointing triangle below the box
              arrowSvg.style.top = `${Math.round(rect.height)}px`;
              const poly = document.createElementNS(svgNS, 'polygon');
              poly.setAttribute('points', `0,0 ${aw/2},${ah} ${aw},0`);
              poly.setAttribute('fill', boxBg);
              poly.setAttribute('stroke', strokeColor);
              poly.setAttribute('stroke-width', '1');
              arrowSvg.appendChild(poly);
            }
            wrapper.appendChild(arrowSvg);
          } catch {}
          return box;
        };

        const yLow = priceToCoord(bar.low);
        const yHigh = priceToCoord(bar.high);
        if (typeof yLow === 'number') makeBox(bullValues, yLow, true);
        if (typeof yHigh === 'number') makeBox(bearValues, yHigh, false);

        // Draw Knox bars-back dotted lines when Knox divergence is active and data is available
        if (enableKnoxvilleDiv && enableKnoxvilleBarsBack) {
          const kval = Number(bar.knox_div_divergence_signal);
          const barsBack = bar.knox_div_bars_back;
          if (Number.isFinite(kval) && kval !== 0 && Array.isArray(barsBack) && barsBack.length > 0) {
            const isBull = kval > 0;
            const anchorY = isBull ? priceToCoord(bar.low) : priceToCoord(bar.high);
            if (typeof anchorY === 'number') {
              const barIdx = barIndexMap.get(bar.time);
              if (typeof barIdx === 'number') {
                // Get distinct N values sorted ascending
                const distinctNs = [...new Set(barsBack)].filter(n => Number.isFinite(n) && n > 0).sort((a, b) => a - b);
                distinctNs.forEach((n, patternIdx) => {
                  const targetIdx = barIdx - n;
                  if (targetIdx < 0 || targetIdx >= mapped.length) return;
                  const targetBar = mapped[targetIdx];
                  const targetX = timeToCoord(targetBar.time);
                  const targetY = priceToCoord(targetBar.close);
                  if (typeof targetX !== 'number' || typeof targetY !== 'number') return;
                  const dashPattern = DASH_PATTERNS[patternIdx % DASH_PATTERNS.length];
                  const line = document.createElementNS(svgNS, 'line');
                  line.setAttribute('x1', String(Math.round(x)));
                  line.setAttribute('y1', String(Math.round(anchorY)));
                  line.setAttribute('x2', String(Math.round(targetX)));
                  line.setAttribute('y2', String(Math.round(targetY)));
                  line.setAttribute('stroke', knoxColor);
                  line.setAttribute('stroke-width', '1.5');
                  line.setAttribute('stroke-dasharray', dashPattern);
                  line.setAttribute('stroke-opacity', '0.7');
                  knoxLinesSvg.appendChild(line);
                });
              }
            }
          }
        }

        // Track max bearish items in current viewport to estimate extra top space needed
        if (bearValues && bearValues.length) {
          maxBearItems = Math.max(maxBearItems, bearValues.length);
        }
      }

      // After laying out labels, ensure we have enough top margin so bearish boxes are visible
      try {
        const contH = container.clientHeight || Math.max(1, Number(height) || 1);
        // Estimate per-item height consistent with chip styles: ~12px per item + inner gaps and padding
        const basePerItem = 12;
        const innerGap = 2;
        const boxPadding = 4;
        const gapAboveHigh = 28; // matches gap used for bearish placement
        const estBoxHeight = maxBearItems > 0
          ? basePerItem * maxBearItems + (maxBearItems > 1 ? (maxBearItems - 1) * innerGap : 0) + boxPadding
          : 0;
        const safety = 4; // small cushion
        const extraTopPx = (maxBearItems > 0) ? (gapAboveHigh + estBoxHeight + safety) : 0;
        const desiredTop = Math.min(0.3, Math.max(priceScaleMarginsRef.current.top || 0.08, extraTopPx / contH));
        if (desiredTop > (priceScaleMarginsRef.current.top || 0)) {
          const bottom = priceScaleMarginsRef.current.bottom || 0.08;
          if (candleSeriesRef.current) {
            try {
              candleSeriesRef.current.priceScale().applyOptions({ scaleMargins: { top: desiredTop, bottom }, borderVisible: false });
            } catch {}
          } else if (chartRef.current) {
            try {
              chartRef.current.applyOptions({ rightPriceScale: { borderVisible: false, scaleMargins: { top: desiredTop, bottom } } });
            } catch {}
          }
          priceScaleMarginsRef.current.top = desiredTop;
        }
      } catch {}
    };

    // Render and subscribe to changes in visible range/resize and interactions affecting price scale
    render();
    const ro = new ResizeObserver(() => scheduleRender());
    try { ro.observe(container); } catch {}
    const unsub = () => { try { chart.timeScale().unsubscribeVisibleTimeRangeChange(scheduleRender); } catch {} };
    chart.timeScale().subscribeVisibleTimeRangeChange(scheduleRender);
    // Wheel and mousemove to catch vertical scale zoom/drag on price scale
    const onWheel = () => scheduleRender();
    const onMove = () => scheduleRender();
    container.addEventListener('wheel', onWheel, { passive: true });
    container.addEventListener('mousemove', onMove, { passive: true });
    // Optionally subscribe to crosshair moves as a fallback trigger
    const onCrosshair = () => scheduleRender();
    try { chart.subscribeCrosshairMove(onCrosshair); } catch {}

    return () => {
      try { ro.disconnect(); } catch {}
      unsub();
      try { container.removeEventListener('wheel', onWheel); } catch {}
      try { container.removeEventListener('mousemove', onMove); } catch {}
      try { chart.unsubscribeCrosshairMove(onCrosshair); } catch {}
      if (rafId) cancelAnimationFrame(rafId);
      try { if (knoxLinesSvg.parentNode) knoxLinesSvg.parentNode.removeChild(knoxLinesSvg); } catch {}
      cleanupPrev();
    };
  }, [enableHiddenDiv, enableRegularDiv, enableKnoxvilleDiv, enableKnoxvilleBarsBack, enableDvlHiddenDiv, enableDvlRegularDiv, theme, data, showCandle, showLine, showVolume, smaPeriods, emaPeriods, kamaPeriods, enableBollinger, enableIchimoku, enableRSIBands, enableMACD, enableRSI, enableADX, height, chartGen, stylePrefs]);

  // Trade signals overlay (BUY/SELL arrows) without recreating the chart
  useEffect(() => {
    const chart = chartRef.current;
    const container = containerRef.current;
    const mapped = Array.isArray(mappedDataRef.current) ? mappedDataRef.current : [];
    if (!chart || !container) return;

    // Cleanup previous overlay and temp series
    const cleanupPrev = () => {
      if (tradeOverlayRef.current && tradeOverlayRef.current.parentNode) {
        try { tradeOverlayRef.current.parentNode.removeChild(tradeOverlayRef.current); } catch {}
        tradeOverlayRef.current = null;
      }
      if (cleanupPrev._coordSeries && chartRef.current) {
        try { chartRef.current.removeSeries(cleanupPrev._coordSeries); } catch {}
      }
    };

    if (!enableTradeSignals || !Array.isArray(mapped) || !mapped.length || !Array.isArray(tradeSignals) || !tradeSignals.length) {
      cleanupPrev();
      return;
    }

    // Ensure clipping
    try { container.style.overflow = 'hidden'; } catch {}

    // Helper parse date similar to main effect
    const parseUTCDate = (dateString) => {
      try {
        if (!dateString) return new Date();
        if (typeof dateString === 'object' && dateString.$date) dateString = dateString.$date;
        if (typeof dateString === 'string' && dateString.includes('-') && dateString.length >= 8) {
          const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
          if (year && month && day) return new Date(Date.UTC(year, month - 1, day));
        }
        const d = new Date(dateString);
        if (!isNaN(d.getTime())) return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
        return new Date();
      } catch { return new Date(); }
    };

    const timeToCoord = (t) => { try { return chart.timeScale().timeToCoordinate(t); } catch { return null; } };
    const coordSeries = candleSeriesRef.current || chart.addLineSeries({ color: 'rgba(0,0,0,0)', lineWidth: 0 });
    cleanupPrev._coordSeries = candleSeriesRef.current ? null : coordSeries;
    const priceToCoord = (p) => { try { return coordSeries.priceToCoordinate(p); } catch { return null; } };

    // Build time->bar map and a helper to find a bar by date (same UTC day)
    const timeMap = new Map(mapped.map(b => [b.time, b]));
    const findBarForDateTs = (ts) => {
      if (timeMap.has(ts)) return timeMap.get(ts);
      const d = new Date(ts * 1000);
      // Try to find bar with same UTC date
      for (let i = mapped.length - 1; i >= 0; i--) {
        const b = mapped[i];
        const bd = new Date(b.time * 1000);
        if (bd.getUTCFullYear() === d.getUTCFullYear() && bd.getUTCMonth() === d.getUTCMonth() && bd.getUTCDate() === d.getUTCDate()) {
          return b;
        }
        if (b.time < ts) break;
      }
      // Fallback: nearest next candle after ts
      for (let i = 0; i < mapped.length; i++) { if (mapped[i].time >= ts) return mapped[i]; }
      return mapped[mapped.length - 1];
    };

    // Create overlay container
    const layer = document.createElement('div');
    layer.style.position = 'absolute';
    layer.style.left = '0';
    layer.style.top = '0';
    layer.style.right = '0';
    layer.style.bottom = '0';
    layer.style.pointerEvents = 'none';
    layer.style.zIndex = 6; // above divergence labels
    layer.style.overflow = 'hidden';
    container.appendChild(layer);
    tradeOverlayRef.current = layer;

  const isDark = theme === 'dark';
  // Compose rgba from hex + alpha if provided; fallback to legacy rgba string or sensible defaults
  const hexToRgba = (hex, alpha = 1) => {
    try {
      if (!hex || typeof hex !== 'string') return null;
      const m = hex.replace('#', '');
      const r = parseInt(m.substring(0, 2), 16);
      const g = parseInt(m.substring(2, 4), 16);
      const b = parseInt(m.substring(4, 6), 16);
      if ([r, g, b].some((v) => Number.isNaN(v))) return null;
      const a = Math.max(0, Math.min(1, Number(alpha) || 0));
      return `rgba(${r}, ${g}, ${b}, ${a})`;
    } catch { return null; }
  };
  const buyHex = stylePrefs?.trade?.buy?.color;
  const buyAlpha = stylePrefs?.trade?.buy?.alpha;
  const sellHex = stylePrefs?.trade?.sell?.color;
  const sellAlpha = stylePrefs?.trade?.sell?.alpha;
  const legacyBuy = stylePrefs?.trade?.buy?.color;
  const legacySell = stylePrefs?.trade?.sell?.color;
  const buyColor = hexToRgba(buyHex, buyAlpha ?? 1) || legacyBuy || 'rgba(121, 182, 143, 1)';
  const sellColor = hexToRgba(sellHex, sellAlpha ?? 1) || legacySell || 'rgba(182, 103, 103, 1)';

    const makeArrow = (x, headY, tailY, type) => {
      const color = type === 'BUY' ? buyColor : sellColor;
      const w = 22; // slightly larger overall width
      const pad = 2; // vertical padding inside svg
      const top = Math.min(headY, tailY) - pad;
      const height = Math.abs(headY - tailY) + pad * 2;
      const left = Math.round(x - w / 2);

      const svgNS = 'http://www.w3.org/2000/svg';
      const svg = document.createElementNS(svgNS, 'svg');
      svg.setAttribute('width', String(w));
  svg.setAttribute('height', String(Math.max(12, Math.round(height))));
  svg.setAttribute('viewBox', `0 0 ${w} ${Math.max(12, Math.round(height))}`);
      svg.style.position = 'absolute';
      svg.style.left = `${left}px`;
      svg.style.top = `${Math.round(top)}px`;
      svg.style.pointerEvents = 'none';
      svg.style.overflow = 'visible';
      svg.style.filter = isDark ? 'drop-shadow(0 1px 1px rgba(0,0,0,0.6))' : 'drop-shadow(0 1px 1px rgba(0,0,0,0.2))';

      // Marker (arrow head)
      const defs = document.createElementNS(svgNS, 'defs');
      const marker = document.createElementNS(svgNS, 'marker');
      const markerId = `arrow-head-${Math.random().toString(36).slice(2, 8)}`;
      marker.setAttribute('id', markerId);
  // Keep marker (head) size constant regardless of stroke width
  marker.setAttribute('markerUnits', 'userSpaceOnUse');
    marker.setAttribute('markerWidth', '14');
    marker.setAttribute('markerHeight', '14');
    marker.setAttribute('refX', '7');
    marker.setAttribute('refY', '7');
      marker.setAttribute('orient', 'auto');
      const headPath = document.createElementNS(svgNS, 'path');
    headPath.setAttribute('d', 'M0,0 L14,7 L0,14 Z');
      headPath.setAttribute('fill', color);
      marker.appendChild(headPath);
      defs.appendChild(marker);
      svg.appendChild(defs);

      // Line from tail to head
      const line = document.createElementNS(svgNS, 'line');
      // Map absolute coords (tail/headY) to local SVG coords: yLocal = absY - top
      const y1 = Math.round(tailY - top);
      const y2 = Math.round(headY - top);
      line.setAttribute('x1', String(w / 2));
      line.setAttribute('y1', String(y1));
      line.setAttribute('x2', String(w / 2));
      line.setAttribute('y2', String(y2));
      line.setAttribute('stroke', color);
  line.setAttribute('stroke-width', String(stylePrefs?.trade?.width || 7));
      line.setAttribute('stroke-linecap', 'round');
      line.setAttribute('marker-end', `url(#${markerId})`);
      svg.appendChild(line);

      return svg;
    };

    const countDivLabels = (bar, bullish) => {
      let c = 0;
      if (enableHiddenDivRef.current) {
        const v = bullish ? bar.div_hid_bullish : bar.div_hid_bearish;
        if (Number.isFinite(Number(v)) && Math.abs(Number(v)) > 0) c += 1;
      }
      if (enableRegularDivRef.current) {
        const v = bullish ? bar.div_reg_bullish : bar.div_reg_bearish;
        if (Number.isFinite(Number(v)) && Math.abs(Number(v)) > 0) c += 1;
      }
      if (enableKnoxvilleDivRef.current) {
        const v = Number(bar.knox_div_divergence_signal);
        if (Number.isFinite(v) && v !== 0) c += 1;
      }
      if (enableDvlHiddenDivRef.current) {
        const v = bullish ? bar.dver_hidden_bullish : bar.dver_hidden_bearish;
        if (Number.isFinite(Number(v)) && Math.abs(Number(v)) > 0) c += 1;
      }
      if (enableDvlRegularDivRef.current) {
        const v = bullish ? bar.dver_regular_bullish : bar.dver_regular_bearish;
        if (Number.isFinite(Number(v)) && Math.abs(Number(v)) > 0) c += 1;
      }
      return c;
    };

    const render = () => {
      try { layer.innerHTML = ''; } catch {}
      const width = container.clientWidth || 0;
      for (const s of tradeSignals) {
        if (!s || (s.action !== 'BUY' && s.action !== 'SELL')) continue;
        const d = parseUTCDate(s.date || s.created_at);
        const ts = Math.floor(d.getTime() / 1000);
        if (!Number.isFinite(ts)) continue;
        const bar = findBarForDateTs(ts);
        if (!bar) continue;
        const x = timeToCoord(bar.time);
        if (typeof x !== 'number' || !(x >= 0 && x <= width)) continue;

        const bullish = s.action === 'BUY';
        const baseYPrice = bullish ? bar.low : bar.high;
        const baseY = priceToCoord(baseYPrice);
        if (typeof baseY !== 'number') continue;

        // Position relative to divergence box when present:
        // - SELL: arrow above the divergence box (which sits above the high)
        // - BUY: arrow below the divergence box (which sits below the low)
        // If no divergence for the respective side, fall back to a small gap from price.
        const baseGap = 16;      // slightly larger gap from price to match size
        const tailLength = 18;   // slightly longer tail for larger arrow
        const divCount = countDivLabels(bar, bullish);
        const divGap = bullish ? 36 : 28; // matches divergence boxes gap logic
        let headY, tailY;
        if (divCount > 0) {
          // Estimate divergence box height (8px font + spacing and padding ~12px per item)
          const boxHeightEst = 12 * divCount + (divCount > 1 ? (divCount - 1) * 2 : 0) + 4; // include inner gaps + padding
          const offset = 9; // slightly larger space between box and arrow head
          headY = bullish
            ? (baseY + divGap + boxHeightEst + offset)
            : (baseY - divGap - boxHeightEst - offset);
          tailY = bullish ? (headY + tailLength) : (headY - tailLength);
        } else {
          // No divergence labels on this side; keep arrow near price
          headY = bullish ? (baseY + baseGap) : (baseY - baseGap);
          tailY = bullish ? (headY + tailLength) : (headY - tailLength);
        }

        const arrow = makeArrow(x, headY, tailY, s.action);
        layer.appendChild(arrow);
      }
    };

    let rafId = 0;
    let scheduled = false;
    const scheduleRender = () => {
      if (scheduled) return;
      scheduled = true;
      rafId = requestAnimationFrame(() => { scheduled = false; render(); });
    };

    render();
    const ro = new ResizeObserver(() => scheduleRender());
    try { ro.observe(container); } catch {}
    const onRange = () => scheduleRender();
    chart.timeScale().subscribeVisibleTimeRangeChange(onRange);
    const onWheel = () => scheduleRender();
    const onMove = () => scheduleRender();
    container.addEventListener('wheel', onWheel, { passive: true });
    container.addEventListener('mousemove', onMove, { passive: true });

    return () => {
      try { ro.disconnect(); } catch {}
      try { chart.timeScale().unsubscribeVisibleTimeRangeChange(onRange); } catch {}
      try { container.removeEventListener('wheel', onWheel); } catch {}
      try { container.removeEventListener('mousemove', onMove); } catch {}
      if (rafId) cancelAnimationFrame(rafId);
      cleanupPrev();
    };
  // Re-run trade overlay whenever divergence visibility changes so arrows reflow immediately
  }, [enableTradeSignals, tradeSignals, theme, data, showCandle, showLine, showVolume, smaPeriods, emaPeriods, kamaPeriods, enableBollinger, enableIchimoku, enableRSIBands, enableMACD, enableRSI, enableADX, // divergence toggles added below
    enableHiddenDiv, enableRegularDiv, enableKnoxvilleDiv, enableDvlHiddenDiv, enableDvlRegularDiv, height, chartGen, stylePrefs]);

  // Ichimoku cloud overlay (fill between senkou A and B)
  useEffect(() => {
    const chart = chartRef.current;
    const container = containerRef.current;
    const mapped = Array.isArray(mappedDataRef.current) ? mappedDataRef.current : [];
    if (!chart || !container || !enableIchimoku || !mapped.length) return;

    // Cleanup previous cloud overlay
    const cleanupPrev = () => {
      if (cloudOverlayRef.current && cloudOverlayRef.current.parentNode) {
        try { cloudOverlayRef.current.parentNode.removeChild(cloudOverlayRef.current); } catch {}
        cloudOverlayRef.current = null;
      }
    };

    cleanupPrev();

  const isDark = theme === 'dark';
  const cloudAlpha = stylePrefs?.ichimoku?.cloudAlpha ?? 0.18;
  const greenFill = `rgba(16,185,129,${cloudAlpha})`;
  const redFill = `rgba(239,68,68,${cloudAlpha})`;

    // Build A and B series points (prefer direct data fields if present)
    const parseUTCDate = (dateString) => {
      try {
        if (!dateString) return new Date();
        if (typeof dateString === 'string' && dateString.includes('-') && dateString.length >= 8) {
          const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
          if (year && month && day) return new Date(Date.UTC(year, month - 1, day));
        }
        const d = new Date(dateString);
        if (!isNaN(d.getTime())) return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
        return new Date();
      } catch { return new Date(); }
    };

    const extractSeries = (fieldKey, indicatorKey) => {
      const hasField = Array.isArray(data) && data.length && data.some(p => typeof p[fieldKey] !== 'undefined');
      const arr = hasField
        ? data.map(p => ({ time: Math.floor(parseUTCDate(p.date).getTime() / 1000), value: Number(p[fieldKey]) }))
        : (indicators[indicatorKey] || []).map(p => ({ time: Math.floor(parseUTCDate(p.date).getTime() / 1000), value: Number(p.value) }));
      return arr.filter(p => Number.isFinite(p.time) && Number.isFinite(p.value)).sort((a, b) => a.time - b.time);
    };

    const aPts = extractSeries('ichimoku_senkou_a', 'ichimoku_senkou_a');
    const bPts = extractSeries('ichimoku_senkou_b', 'ichimoku_senkou_b');
    if (!aPts.length || !bPts.length) return;

    // Build map for fast lookup and common times array
    const bMap = new Map(bPts.map(p => [p.time, p.value]));
    const common = [];
    for (const p of aPts) {
      const v = bMap.get(p.time);
      if (typeof v === 'number' && Number.isFinite(v)) common.push({ time: p.time, a: p.value, b: v });
    }
    if (!common.length) return;

    // Create overlay svg
    const layer = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    const w = container.clientWidth || 0;
    const h = container.clientHeight || 0;
    layer.setAttribute('width', String(Math.max(1, w)));
    layer.setAttribute('height', String(Math.max(1, h)));
    layer.style.position = 'absolute';
    layer.style.left = '0';
    layer.style.top = '0';
    layer.style.right = '0';
    layer.style.bottom = '0';
    layer.style.pointerEvents = 'none';
    layer.style.zIndex = 2; // below divergence (5) and trades (6)
    container.appendChild(layer);
    cloudOverlayRef.current = layer;

    const seriesForCoords = candleSeriesRef.current || chart.addLineSeries({ color: 'rgba(0,0,0,0)', lineWidth: 0 });
    const priceToCoord = (p) => { try { return seriesForCoords.priceToCoordinate(p); } catch { return null; } };
    const timeToCoord = (t) => { try { return chart.timeScale().timeToCoordinate(t); } catch { return null; } };

    let tmpCoordSeries = null;
    if (!candleSeriesRef.current) tmpCoordSeries = seriesForCoords;

    const draw = () => {
      while (layer.firstChild) layer.removeChild(layer.firstChild);
      const w = container.clientWidth || 0;
      const h = container.clientHeight || 0;
      layer.setAttribute('width', String(Math.max(1, w)));
      layer.setAttribute('height', String(Math.max(1, h)));

      // Build coordinates and split into segments where ordering stays the same
      const coords = [];
      for (const { time, a, b } of common) {
        const x = timeToCoord(time);
        const ya = priceToCoord(a);
        const yb = priceToCoord(b);
        if (typeof x !== 'number' || typeof ya !== 'number' || typeof yb !== 'number') continue;
        if (!(x >= 0 && x <= w)) continue;
        coords.push({ x, ya, yb });
      }
      if (coords.length < 2) return;

      let i = 0;
      while (i < coords.length - 1) {
        const isGreen = coords[i].ya <= coords[i].yb ? false : true; // A above B => green; else red
        const segA = [];
        const segB = [];
        // Collect contiguous segment until ordering flips
        let j = i;
        while (j < coords.length) {
          const c = coords[j];
          const above = c.ya < c.yb; // smaller y is higher visually
          const greenNow = above; // A above B => green
          if (j > i && greenNow !== (coords[i].ya < coords[i].yb)) break;
          segA.push([c.x, c.ya]);
          segB.push([c.x, c.yb]);
          j++;
        }
        // Create polygon for this segment
        if (segA.length >= 2) {
          const poly = document.createElementNS('http://www.w3.org/2000/svg', 'path');
          const path = [];
          path.push(`M ${segA[0][0].toFixed(1)} ${segA[0][1].toFixed(1)}`);
          for (let k = 1; k < segA.length; k++) path.push(`L ${segA[k][0].toFixed(1)} ${segA[k][1].toFixed(1)}`);
          for (let k = segB.length - 1; k >= 0; k--) path.push(`L ${segB[k][0].toFixed(1)} ${segB[k][1].toFixed(1)}`);
          path.push('Z');
          poly.setAttribute('d', path.join(' '));
          poly.setAttribute('fill', (coords[i].ya < coords[i].yb) ? greenFill : redFill);
          poly.setAttribute('stroke', 'none');
          layer.appendChild(poly);
        }
        i = Math.max(j, i + 1);
      }
    };

    let rafId = 0;
    let scheduled = false;
    const scheduleDraw = () => { if (!scheduled) { scheduled = true; rafId = requestAnimationFrame(() => { scheduled = false; draw(); }); } };
    draw();
    const ro = new ResizeObserver(() => scheduleDraw());
    try { ro.observe(container); } catch {}
    const onRange = () => scheduleDraw();
    chart.timeScale().subscribeVisibleTimeRangeChange(onRange);
    const onWheel = () => scheduleDraw();
    const onMove = () => scheduleDraw();
    container.addEventListener('wheel', onWheel, { passive: true });
    container.addEventListener('mousemove', onMove, { passive: true });

    return () => {
      try { ro.disconnect(); } catch {}
      try { chart.timeScale().unsubscribeVisibleTimeRangeChange(onRange); } catch {}
      try { container.removeEventListener('wheel', onWheel); } catch {}
      try { container.removeEventListener('mousemove', onMove); } catch {}
      if (rafId) cancelAnimationFrame(rafId);
      if (tmpCoordSeries && chartRef.current) {
        try { chartRef.current.removeSeries(tmpCoordSeries); } catch {}
      }
      cleanupPrev();
    };
  }, [enableIchimoku, indicators, data, theme, height, chartGen, stylePrefs]);

  // Listen for style preference updates
  useEffect(() => {
    const onUpdate = () => {
      try {
        const raw = localStorage.getItem('borsai.indicatorStylePrefs.v1');
        setStylePrefs(raw ? JSON.parse(raw) : null);
      } catch {}
    };
    window.addEventListener('borsai.indicatorStylePrefs.updated', onUpdate);
    return () => window.removeEventListener('borsai.indicatorStylePrefs.updated', onUpdate);
  }, []);

  // Toolbar UI and indicators panel
  const isDark = theme === 'dark';
  const btnBase = 'px-3 py-1 text-xs rounded-md border';
  const btnActive = isDark ? 'bg-blue-600 border-blue-500 text-white' : 'bg-blue-600 border-blue-600 text-white';
  const btnInactive = isDark ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-white border-gray-300 text-gray-700';

  // Compute toolbar trend text showing both Model 1 & Model 2 signals
  // Real current signals live in model_stats.current_signals (top-level model1/2_signal are stale defaults)
  const toolbarTrend = computeToolbarTrend(stockInfo, predictions, isDark);

  const ensureAtLeastOne = (nextCandle, nextLine) => {
    if (!nextCandle && !nextLine) return [true, false];
    return [nextCandle, nextLine];
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
        <button
          type="button"
          className={`${btnBase} ${showCandle ? btnActive : btnInactive}`}
          title="Candle (C)"
          onClick={() => {
            const [nc, nl] = ensureAtLeastOne(!showCandle, showLine);
            setShowCandle(nc); setShowLine(nl);
          }}
        >Candle</button>
        <button
          type="button"
          className={`${btnBase} ${showLine ? btnActive : btnInactive}`}
          title="Line (L)"
          onClick={() => {
            const [nc, nl] = ensureAtLeastOne(showCandle, !showLine);
            setShowCandle(nc); setShowLine(nl);
          }}
        >Line</button>
        <button
          type="button"
          className={`${btnBase} ${showVolume ? btnActive : btnInactive}`}
          title="Volume (V)"
          onClick={() => setShowVolume(v => !v)}
        >Volume</button>
        <button
          type="button"
          className={`${btnBase} ${showAISignalsPanel ? btnActive : btnInactive}`}
          title="AI Signals"
          onClick={() => setShowAISignalsPanel(p => !p)}
        >AI Signals</button>
        <button
          type="button"
          className={`${btnBase} ${showIndicatorsPanel ? btnActive : btnInactive}`}
          title="Technical Indicators"
          onClick={() => setShowIndicatorsPanel(p => !p)}
        >Technical Indicators</button>
        </div>
        {toolbarTrend && (
          <div
            className="text-xs font-normal"
            style={{
              whiteSpace: 'nowrap',
              color: isDark ? '#d1d5db' : '#111827'
            }}
          >
            <span>AI Signals</span>
            <span className="relative inline-flex items-center group">
              <button
                type="button"
                aria-label="Automated model output. Not investment advice."
                className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 text-[10px] font-semibold leading-none text-gray-600 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
              >
                i
              </button>
              <div className="pointer-events-none absolute left-0 top-full z-10 mt-2 w-max whitespace-pre-line rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-[12px] text-gray-700 opacity-0 shadow-sm transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200">
                {'Automated model output.\nNot investment advice.'}
              </div>
            </span>
            {toolbarTrend.dateLabel && <span> — {toolbarTrend.dateLabel}</span>}
            <span>: </span>
            {toolbarTrend.model1 && (
              <>
                <span>Model 1 </span>
                <span style={{ color: toolbarTrend.model1.color, fontWeight: 'bold' }}>{toolbarTrend.model1.icon} {toolbarTrend.model1.signal}</span>
              </>
            )}
            {toolbarTrend.model1 && toolbarTrend.model2 && <span> · </span>}
            {toolbarTrend.model2 && (
              <>
                <span>Model 2 </span>
                <span style={{ color: toolbarTrend.model2.color, fontWeight: 'bold' }}>{toolbarTrend.model2.icon} {toolbarTrend.model2.signal}</span>
              </>
            )}
            {(toolbarTrend.model1 || toolbarTrend.model2) && toolbarTrend.modelEnsemble && <span> · </span>}
            {toolbarTrend.modelEnsemble && (
              <>
                <span>Model Ens </span>
                <span style={{ color: toolbarTrend.modelEnsemble.color, fontWeight: 'bold' }}>{toolbarTrend.modelEnsemble.icon} {toolbarTrend.modelEnsemble.signal}</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* AI Signals Panel */}
      {showAISignalsPanel && (
        <div className={`${isDark ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-300'} border rounded-md p-2 mb-2 text-xs`}>
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-1">
              <input type="checkbox" checked={enableTradeSignals} onChange={() => setEnableTradeSignals(v => !v)} />
              <span title="Buy / Sell (T)">Buy / Sell</span>
            </label>

            {/* Model selection combobox */}
            {Array.isArray(availableModels) && availableModels.length > 0 && (
              <div className="flex items-center gap-1">
                <label htmlFor="ai-model-select" className="text-xs">Model:</label>
                <select
                  id="ai-model-select"
                  value={selectedModel}
                  onChange={(e) => onChangeModel ? onChangeModel(e.target.value) : null}
                  className={`px-2 py-1 text-xs rounded-md border focus:outline-none ${
                    isDark
                      ? 'bg-gray-800 border-gray-700 text-gray-200'
                      : 'bg-white border-gray-300 text-gray-800'
                  }`}
                >
                  {availableModels.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Live Signal UI removed; live marker is always enabled */}
          </div>
        </div>
      )}

      {showIndicatorsPanel && (
        <div className={`${isDark ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-300'} border rounded-md p-2 mb-2 text-xs`}>
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-1">
              <input type="checkbox" checked={enableBollinger} onChange={() => setEnableBollinger(b => !b)} />
              <span title="Bollinger Bands (B)">Bollinger</span>
            </label>
            <label className="inline-flex items-center gap-1">
              <input type="checkbox" checked={enableIchimoku} onChange={() => setEnableIchimoku(b => !b)} />
              <span title="Ichimoku (I)">Ichimoku</span>
            </label>
            <label className="inline-flex items-center gap-1">
              <input type="checkbox" checked={enableRSIBands} onChange={() => setEnableRSIBands(b => !b)} />
              <span title="RSI Bands (RB)">RSI Bands</span>
            </label>
            <label className="inline-flex items-center gap-1">
              <input type="checkbox" checked={enableMACD} onChange={() => setEnableMACD(b => !b)} />
              <span title="MACD (M)">MACD</span>
            </label>
            <label className="inline-flex items-center gap-1">
              <input type="checkbox" checked={enableRSI} onChange={() => setEnableRSI(b => !b)} />
              <span title="RSI (R)">RSI</span>
            </label>
            <label className="inline-flex items-center gap-1">
              <input type="checkbox" checked={enableADX} onChange={() => setEnableADX(b => !b)} />
              <span title="ADX">ADX</span>
            </label>
            <div ref={emaDropdownRef} className="relative inline-flex items-center gap-1">
              <label className="inline-flex items-center gap-1">
                <input
                  ref={emaAllRef}
                  type="checkbox"
                  checked={emaPeriods.length === MA_OPTIONS.length}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setEmaPeriods(checked ? [...MA_OPTIONS] : []);
                  }}
                />
                <span title="All EMAs (E)">All EMAs</span>
              </label>
              <button
                type="button"
                aria-label="Toggle EMA options"
                className={`ml-1 px-1.5 py-0.5 rounded border ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-300 text-gray-800'}`}
                onClick={() => setShowEmaOptions(v => !v)}
              >{showEmaOptions ? '▴' : '▾'}</button>
              {showEmaOptions && (
                <div className={`absolute top-full left-0 mt-1 w-28 z-20 rounded-md border shadow ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200 text-gray-800'}`}>
                  {MA_OPTIONS.map(p => (
                    <label key={`ema-${p}`} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700">
                      <input
                        type="checkbox"
                        checked={emaPeriods.includes(p)}
                        onChange={() => {
                          setEmaPeriods(prev => {
                            const set = new Set(prev);
                            if (set.has(p)) set.delete(p); else set.add(p);
                            // keep order according to MA_OPTIONS
                            return MA_OPTIONS.filter(x => set.has(x));
                          });
                        }}
                      />
                      <span>{p}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div ref={smaDropdownRef} className="relative inline-flex items-center gap-1">
              <label className="inline-flex items-center gap-1">
                <input
                  ref={smaAllRef}
                  type="checkbox"
                  checked={smaPeriods.length === MA_OPTIONS.length}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSmaPeriods(checked ? [...MA_OPTIONS] : []);
                  }}
                />
                <span title="All SMAs (S)">All SMAs</span>
              </label>
              <button
                type="button"
                aria-label="Toggle SMA options"
                className={`ml-1 px-1.5 py-0.5 rounded border ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-300 text-gray-800'}`}
                onClick={() => setShowSmaOptions(v => !v)}
              >{showSmaOptions ? '▴' : '▾'}</button>
              {showSmaOptions && (
                <div className={`absolute top-full left-0 mt-1 w-28 z-20 rounded-md border shadow ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200 text-gray-800'}`}>
                  {MA_OPTIONS.map(p => (
                    <label key={`sma-${p}`} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700">
                      <input
                        type="checkbox"
                        checked={smaPeriods.includes(p)}
                        onChange={() => {
                          setSmaPeriods(prev => {
                            const set = new Set(prev);
                            if (set.has(p)) set.delete(p); else set.add(p);
                            return MA_OPTIONS.filter(x => set.has(x));
                          });
                        }}
                      />
                      <span>{p}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div ref={kamaDropdownRef} className="relative inline-flex items-center gap-1">
              <label className="inline-flex items-center gap-1">
                <input
                  ref={kamaAllRef}
                  type="checkbox"
                  checked={kamaPeriods.length === MA_OPTIONS.length}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setKamaPeriods(checked ? [...MA_OPTIONS] : []);
                  }}
                />
                <span title="All KAMAs (A)">All KAMAs</span>
              </label>
              <button
                type="button"
                aria-label="Toggle KAMA options"
                className={`ml-1 px-1.5 py-0.5 rounded border ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-300 text-gray-800'}`}
                onClick={() => setShowKamaOptions(v => !v)}
              >{showKamaOptions ? '▴' : '▾'}</button>
              {showKamaOptions && (
                <div className={`absolute top-full left-0 mt-1 w-28 z-20 rounded-md border shadow ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200 text-gray-800'}`}>
                  {MA_OPTIONS.map(p => (
                    <label key={`kama-${p}`} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700">
                      <input
                        type="checkbox"
                        checked={kamaPeriods.includes(p)}
                        onChange={() => {
                          setKamaPeriods(prev => {
                            const set = new Set(prev);
                            if (set.has(p)) set.delete(p); else set.add(p);
                            return MA_OPTIONS.filter(x => set.has(x));
                          });
                        }}
                      />
                      <span>{p}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <span className="mx-2 opacity-60">|</span>
            <label className="inline-flex items-center gap-1">
              <input type="checkbox" checked={enableDvlHiddenDiv} onChange={() => setEnableDvlHiddenDiv(b => !b)} />
              <span title="Hidden Divergences (J)">Hidden Div.</span>
            </label>
            <label className="inline-flex items-center gap-1">
              <input type="checkbox" checked={enableDvlRegularDiv} onChange={() => setEnableDvlRegularDiv(b => !b)} />
              <span title="Regular Divergences (N)">Regular Div.</span>
            </label>
            <span className="mx-2 opacity-60">|</span>
            <label className="inline-flex items-center gap-1">
              <input type="checkbox" checked={enableHiddenDiv} onChange={() => setEnableHiddenDiv(b => !b)} />
              <span title="Adaptive Hidden Divergences (H)">Adaptive Hidden Div.</span>
            </label>
            <label className="inline-flex items-center gap-1">
              <input type="checkbox" checked={enableRegularDiv} onChange={() => setEnableRegularDiv(b => !b)} />
              <span title="Adaptive Regular Divergences (G)">Adaptive Regular Div.</span>
            </label>
            <span className="mx-2 opacity-60">|</span>
            <label className="inline-flex items-center gap-1">
              <input type="checkbox" checked={enableKnoxvilleDiv} onChange={() => setEnableKnoxvilleDiv(b => !b)} />
              <span title="Knoxville Divergence (K)">Knoxville Div.</span>
            </label>
            <label className="inline-flex items-center gap-1">
              <input type="checkbox" checked={enableKnoxvilleBarsBack} onChange={() => setEnableKnoxvilleBarsBack(b => !b)} />
              <span title="Show Knoxville bars-back dotted lines">Knoxville Bars Back</span>
            </label>
          </div>
        </div>
      )}

      <div ref={containerRef} className="w-full" style={{ height }} />

      {/* Period selector */}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {['1M','3M','6M','YTD','1Y','ALL'].map((p) => {
          const active = selectedPeriod === p;
          const base = 'px-2 py-0.5 text-xs rounded-md border';
          const cls = active
            ? (isDark ? 'bg-blue-600 border-blue-500 text-white' : 'bg-blue-600 border-blue-600 text-white')
            : (isDark ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100');
          return (
            <button
              key={p}
              type="button"
              className={`${base} ${cls}`}
              onClick={() => {
                // User explicitly selected a period; clear any saved manual range and disable auto-follow
                try { savedRangeRef.current = null; } catch {}
                shouldAutoScrollRef.current = false;
                setSelectedPeriod(p);
              }}
              title={`Show ${p} period`}
            >{p}</button>
          );
        })}
      </div>
    </div>
  );
};

export default CandlestickChart;